import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Alert,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Divider,
  Stack,
  Radio,
  RadioGroup,
  FormGroup,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Grid,
  TablePagination,
  useTheme,
  useMediaQuery,
  Autocomplete,
  Popover,
  Snackbar,
  Menu,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import FilePreviewDialog from '../components/FilePreviewDialog';
import FileThumbnail from '../components/FileThumbnail';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  CheckCircle as ApproveIcon,
  CloudUpload as UploadIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Visibility as ApproverIcon,
  Done as CompleteReviewIcon,
  ArchiveOutlined as WithdrawIcon,
  Visibility as ViewIcon,
  Visibility,
  VisibilityOff,
  Description as SourceIcon,
  Refresh as RefreshIcon,
  IosShare as ShareIcon,
  ContentCopy as CopyIcon,
  Security as ShieldIcon,
  Lock as LockIcon,
  VpnKey as KeyIcon,
  Timer as TimerIcon,
  AccountTree as ChartIcon,
  FormatListBulleted as ListIcon,
  Close as CloseIcon,
  LockOutlined as RestrictedIcon,
  Public as PublicIcon,
  Check as CheckIcon,
  Close as XIcon,
  Description as DescriptionIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  SortByAlpha as SortByAlphaIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import dayjs from 'dayjs';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import PermissionGate from '../components/PermissionGate';
import { useStandard } from '../contexts/StandardContext';
import DocumentHierarchyFlow from '../components/DocumentHierarchyFlow';
import Loading from '../components/Loading';


const STATUS_CONFIG = {
  draft: { color: 'default', label: 'Draft' },
  in_review: { color: 'warning', label: 'In Review' },
  pending_approval: { color: 'info', label: 'Pending Approval' },
  approved: { color: 'success', label: 'Approved' },
  archived: { color: 'default', label: 'Archived' },
  withdrawn: { color: 'default', label: 'Withdrawn' }
};

const STATUS_INDICATORS = {
  draft: '#64748b',
  in_review: '#f59e0b',
  pending_approval: '#3b82f6',
  approved: '#10b981',
  withdrawn: '#ef4444'
};

const DocumentsPage = () => {
  const { hasPermission, user } = useAuth();
  const theme = useTheme();
  const savingRef = useRef(false);
  const titleRef = useRef('');
  const descRef = useRef('');

  const { selectedStandard, availableStandards } = useStandard();
  const defaultStandard = selectedStandard || availableStandards?.[0] || 'iso9001';

  const [documents, setDocuments] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [jobPositions, setJobPositions] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuDoc, setActionMenuDoc] = useState(null);
  const [openReviewDialog, setOpenReviewDialog] = useState(false);
  const [openVersionDialog, setOpenVersionDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [showWorkflowHistory, setShowWorkflowHistory] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [viewFileData, setViewFileData] = useState(null);
  const [isViewLoading, setIsViewLoading] = useState(false);

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [updatedDocIds, setUpdatedDocIds] = useState(new Set());
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  // Use a ref to store current documents for change detection without triggering re-fetches
  const docsRef = React.useRef(documents);
  useEffect(() => {
    docsRef.current = documents;
  }, [documents]);

  // Bulk Selection State
  const [selectedDocIds, setSelectedDocIds] = useState(new Set());
  const [showBulkAccessDialog, setShowBulkAccessDialog] = useState(false);
  const [bulkAccessSettings, setBulkAccessSettings] = useState({
    access_level: 'organization_wide',
    selected_process_ids: [],
    access_rules: [] // { job_position_id, title, can_view, can_download, can_edit }
  });
  const [reviewAction, setReviewAction] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    docId: null,
    docTitle: ''
  });
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);
  const [saving, setSaving] = useState(false);

  const [reviewDueDate, setReviewDueDate] = useState(dayjs().add(7, 'day').format('YYYY-MM-DD'));
  const [openWithdrawDialog, setOpenWithdrawDialog] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [sortOrder, setSortOrder] = useState('default');
  const [filters, setFilters] = useState({
    status: 'all',
    category: 'all',
    document_type: defaultStandard || 'all',
    search: '',
    owner: 'all',
    showWithdrawn: false
  });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'policy',
    classification: 'internal',
    document_type: defaultStandard,
    process_id: '',
    job_position_id: '',
    document_code: '',
    access_level: 'organization_wide',
    is_confidential: false,
    require_acknowledgment: false,
    document_access: [], // { job_position_id, can_view, can_download, can_edit }
    next_update_at: dayjs().add(1, 'year').format('YYYY-MM-DD'),
    version_number: '1.0'
  });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadSourceFile, setUploadSourceFile] = useState(null); // [NEW] Source file state
  const [uploadChangeSummary, setUploadChangeSummary] = useState('');
  const [uploadVersionNumber, setUploadVersionNumber] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'chart'
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // File validation constants
  const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt', '.rtf', '.png', '.jpg', '.jpeg', '.gif', '.webp', '.xml'];
  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

  const validateFile = (file) => {
    if (!file) return null;
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return `This format (${ext}) is not supported by the system. Supported formats: PDF, Word (.doc, .docx), Excel (.xls, .xlsx), Text (.txt, .rtf), XML, and Images (.png, .jpg, .gif, .webp)`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File "${file.name}" is too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum size is 50MB`;
    }
    return null;
  };

  const handleFileSelect = (file) => {
    const error = validateFile(file);
    if (error) {
      setSnackbar({ open: true, message: error, severity: 'error' });
      return;
    }
    setUploadFile(file);
  };

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [totalDocs, setTotalDocs] = useState(0);
  const [serverCounts, setServerCounts] = useState({
    all: 0,
    draft: 0,
    inReview: 0,
    pendingApproval: 0,
    approved: 0,
    withdrawn: 0
  });

  const [quickDateAnchor, setQuickDateAnchor] = useState(null);
  const [quickDateDoc, setQuickDateDoc] = useState(null);
  const [quickDateValue, setQuickDateValue] = useState('');

  const [users, setUsers] = useState([]); // Added users state

  const ownerOptions = useMemo(() => {
    const unique = new Map();
    documents.forEach((doc) => {
      if (doc.created_by_user) {
        unique.set(doc.created_by_user.id, doc.created_by_user);
      }
    });
    return Array.from(unique.values());
  }, [documents]);



  const formatDate = (value) => (value ? dayjs(value).format('MMM D, YYYY') : '—');

  // localStorage cache utilities
  const CACHE_PREFIX = 'documents_cache_';
  const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes default (though we always background refresh)

  const getCacheKey = (f, p, r) => {
    return `${CACHE_PREFIX}${f.status}-${f.category}-${f.document_type}-${f.owner}-${user?.role}-${p}-${r}`;
  };

  const saveToLocalStorage = (key, data) => {
    try {
      const cacheData = {
        timestamp: Date.now(),
        data: data
      };
      localStorage.setItem(key, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Failed to save documents to localStorage:', error);
    }
  };

  const getFromLocalStorage = (key) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    if (selectedStandard) {
      setFilters((prev) => ({
        ...prev,
        document_type: selectedStandard
      }));
      setFormData((prev) => ({
        ...prev,
        document_type: selectedStandard
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStandard]);

  useEffect(() => {
    const loadData = async () => {
      // Don't set global loading here if we want instant cache render
      await Promise.all([fetchDocuments(), fetchProcesses(), fetchJobPositions(), fetchUsers()]);
      setLoading(false);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, rowsPerPage]);

  const fetchJobPositions = async () => {
    try {
      const res = await api.get('/job-positions');
      setJobPositions(res.data.positions || []);
    } catch (error) {
      console.error('Failed to fetch job positions:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchProcesses = async () => {
    try {
      const [procRes, deptRes] = await Promise.all([
        api.get('/processes'),
        api.get('/teams')
      ]);
      setProcesses(procRes.data.processes || []);

      let allDepts = deptRes.data.teams || [];

      // Filter departments for non-admins to only show their own
      if (!hasPermission('documents.view_all')) {
        const relevantDeptIds = new Set(documents.map(d => d.job_position_id).filter(Boolean));
        allDepts = allDepts.filter(d => relevantDeptIds.has(d.id));
      }

      setDepartments(allDepts);
    } catch (error) {
      console.error('Failed to fetch metadata:', error);
    }
  };

  const fetchDocuments = async () => {
    const currentKey = getCacheKey(filters, page, rowsPerPage);
    const cachedData = getFromLocalStorage(currentKey);

    try {
      if (cachedData && cachedData.data) {
        setDocuments(cachedData.data.documents || []);
        setTotalDocs(cachedData.data.pagination?.total || cachedData.data.documents?.length || 0);
        if (cachedData.data.counts) {
          const cachedCounts = cachedData.data.counts;
          setServerCounts({
            all: cachedCounts.all || 0,
            draft: cachedCounts.draft || 0,
            inReview: cachedCounts.in_review || 0,
            pendingApproval: cachedCounts.pending_approval || 0,
            approved: cachedCounts.approved || 0,
            withdrawn: cachedCounts.withdrawn || 0
          });
        }
        setLoading(false);
        setRefreshing(true);
      } else {
        if (documents.length === 0) setLoading(true);
      }

      const params = new URLSearchParams();
      params.append('page', page + 1);
      params.append('limit', rowsPerPage);

      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.category !== 'all') params.append('category', filters.category);
      if (filters.document_type !== 'all') params.append('document_type', filters.document_type);
      if (filters.search) params.append('search', filters.search);
      if (filters.owner !== 'all') params.append('owner_user_id', filters.owner);
      if (filters.showWithdrawn) params.append('include_withdrawn', 'true');

      const response = await api.get(`/documents?${params.toString()}`);
      const freshDocs = response.data.documents || [];
      const freshTotal = response.data.pagination?.total || freshDocs.length;
      const freshCounts = response.data.counts || null;

      // Detect visual changes using the ref
      const currentDocs = docsRef.current;
      if (currentDocs.length > 0) {
        const changedIds = new Set();
        freshDocs.forEach(newDoc => {
          const oldDoc = currentDocs.find(d => d.id === newDoc.id);
          if (!oldDoc || (
            oldDoc.status !== newDoc.status ||
            oldDoc.version_number !== newDoc.version_number ||
            oldDoc.title !== newDoc.title
          )) {
            changedIds.add(newDoc.id);
          }
        });

        if (changedIds.size > 0) {
          setUpdatedDocIds(changedIds);
          setTimeout(() => setUpdatedDocIds(new Set()), 8000);
        }
      }

      setDocuments(freshDocs);
      setTotalDocs(freshTotal);
      if (freshCounts) {
        setServerCounts({
          all: freshCounts.all || 0,
          draft: freshCounts.draft || 0,
          inReview: freshCounts.in_review || 0,
          pendingApproval: freshCounts.pending_approval || 0,
          approved: freshCounts.approved || 0,
          withdrawn: freshCounts.withdrawn || 0
        });
      }
      setLastRefreshedAt(new Date());
      saveToLocalStorage(currentKey, response.data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoading(false);
      setTimeout(() => setRefreshing(false), 500);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(0);

    // Sync Tabs with Status Filter
    let newStatus = 'all';
    if (newValue === 1) newStatus = 'pending_approval';
    if (newValue === 2) newStatus = 'approved';

    setFilters(prev => ({ ...prev, status: newStatus }));
  };

  const handleSaveDocument = async () => {
    if ((!editingDoc && !canCreate) || (editingDoc && !canUpdate)) return;
    if (savingRef.current) return; // Prevent double submission (ref is synchronous)
    // Sync ref values in case user didn't blur the text fields
    const saveData = {
      ...formData,
      title: titleRef.current || formData.title,
      description: descRef.current !== undefined ? descRef.current : formData.description
    };
    if (!saveData.title.trim()) {
      setSnackbar({ open: true, message: 'Document name is required', severity: 'error' });
      return;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      let docId;
      if (editingDoc) {
        await api.put(`/documents/${editingDoc.id}`, saveData);
        docId = editingDoc.id;
      } else {
        const res = await api.post('/documents', saveData);
        docId = res.data.document.id;
      }

      // Upload file as version (new doc = initial version, edit = new version)
      if (uploadFile && docId) {
        const versionData = new FormData();
        versionData.append('file', uploadFile);
        if (uploadSourceFile) versionData.append('source_file', uploadSourceFile);
        versionData.append('change_summary', editingDoc ? 'New version upload' : 'Initial version');
        versionData.append('version_number', formData.version_number || '1.0');

        await api.post(`/documents/${docId}/versions`, versionData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      setOpenDialog(false);
      setEditingDoc(null);
      setFormData({
        title: '',
        description: '',
        category: 'policy',
        classification: 'internal',
        document_type: defaultStandard,
        process_id: '',
        document_code: '',
        access_level: 'organization_wide',
        is_confidential: false,
        require_acknowledgment: false,
        document_access: [],
        temp_use_process_access: true,
        temp_selected_processes: [],
        next_update_at: dayjs().add(1, 'year').format('YYYY-MM-DD'),
        version_number: '1.0'
      });
      setUploadFile(null);
      setUploadSourceFile(null);
      fetchDocuments();
      setSnackbar({ open: true, message: editingDoc ? 'Document updated successfully' : 'Document created successfully', severity: 'success' });
    } catch (error) {
      console.error('Failed to save document:', error);
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to save document', severity: 'error' });
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const generateDocumentCode = () => {
    const typeMap = {
      policy: 'POL',
      procedure: 'PRO',
      work_instruction: 'WIN',
      form: 'FRM',
      record: 'REC'
    };
    const code = `${typeMap[formData.category] || 'DOC'}-${Math.floor(1000 + Math.random() * 9000)}`;
    setFormData({ ...formData, document_code: code });
  };

  const handleUnlinkProcess = async (doc) => {
    if (!canUpdate) return;
    if (!window.confirm(`Are you sure you want to unlink "${doc.title}" from its process?`)) return;

    try {
      await api.put(`/documents/${doc.id}`, {
        ...doc,
        process_id: null
      });
      fetchDocuments();
    } catch (error) {
      console.error('Failed to unlink document:', error);
      setSnackbar({ open: true, message: 'Failed to unlink document', severity: 'error' });
    }
  };

  const handleLinkDocument = async (docId, type, sourceId) => {
    try {
      const doc = documents.find(d => d.id == docId); // subtle loose equality for safety
      if (!doc) return;

      const updateData = {};
      if (type === 'process') {
        updateData.process_id = sourceId;
        // Inherit department from process
        const linkedProcess = processes.find(p => p.id == sourceId);
        if (linkedProcess && linkedProcess.job_position_id) {
          updateData.job_position_id = linkedProcess.job_position_id;
        }
      } else if (type === 'department') {
        updateData.job_position_id = sourceId;
        // Clear process_id to fully dissociate from previous context (e.g. "delink from Production Process")
        updateData.process_id = null;
      } else if (type === 'document') {
        updateData.parent_document_id = sourceId;

        // Inherit context (Department & Process) from parent document
        const parentDoc = documents.find(d => d.id == sourceId);
        if (parentDoc) {
          if (parentDoc.job_position_id) {
            updateData.job_position_id = parentDoc.job_position_id;
          }
          if (parentDoc.process_id) {
            updateData.process_id = parentDoc.process_id;
          }
        }
      }

      await api.put(`/documents/${docId}`, { ...doc, ...updateData });
      fetchDocuments();
    } catch (error) {
      console.error("Failed to link document", error);
    }
  };

  const handleUnlinkDocument = async (docId) => {
    try {
      const doc = documents.find(d => d.id == docId);
      if (!doc) return;

      const updateData = {
        process_id: null,
        job_position_id: null,
        parent_document_id: null
      };

      await api.put(`/documents/${docId}`, { ...doc, ...updateData });
      fetchDocuments();
    } catch (error) {
      console.error("Failed to unlink document", error);
    }
  };

  const handleEditClick = (doc) => {
    setEditingDoc(doc);
    // Transform access_rules (from backend) to document_access (state)
    const accessRules = doc.access_rules || [];

    setFormData({
      title: doc.title,
      description: doc.description || '',
      category: doc.category,
      classification: doc.classification || 'internal',
      document_type: doc.document_type || defaultStandard,
      process_id: doc.process_id || (doc.process ? doc.process.id : ''),
      document_code: doc.document_code || '',
      access_level: doc.access_level || 'restricted',
      is_confidential: doc.is_confidential || false,
      require_acknowledgment: doc.require_acknowledgment || false,
      document_access: accessRules.map(r => ({
        job_position_id: r.job_position_id,
        can_view: r.can_view,
        can_download: r.can_download,
        can_edit: r.can_edit
      })),
      temp_use_process_access: true,
      temp_selected_processes: (doc.process_id || (doc.process && doc.process.id)) ? [processes.find(p => p.id === (doc.process_id || (doc.process && doc.process.id)))].filter(Boolean) : [],
      next_update_at: doc.next_update_at ? dayjs(doc.next_update_at).format('YYYY-MM-DD') : dayjs().add(1, 'year').format('YYYY-MM-DD'),
      version_number: (doc.current_version && !isNaN(parseFloat(doc.current_version))) ? (parseFloat(doc.current_version) + 1).toFixed(1) : '1.0'
    });
    titleRef.current = doc.title || '';
    descRef.current = doc.description || '';
    setUploadFile(null);
    setUploadSourceFile(null);
    setOpenDialog(true);
  };

  const handleQuickUpdateDate = (event, doc) => {
    setQuickDateAnchor(event.currentTarget);
    setQuickDateDoc(doc);
    setQuickDateValue(doc.next_update_at ? dayjs(doc.next_update_at).format('YYYY-MM-DD') : dayjs().add(1, 'year').format('YYYY-MM-DD'));
  };

  const saveQuickDate = async () => {
    if (!quickDateDoc) return;
    try {
      await api.put(`/documents/${quickDateDoc.id}`, {
        next_update_at: quickDateValue
      });
      setQuickDateAnchor(null);
      fetchDocuments();
    } catch (error) {
      console.error('Failed to update date:', error);
      setSnackbar({ open: true, message: error.response?.data?.error || error.message || 'Failed to update date', severity: 'error' });
    }
  };

  const handleAccessChange = (positionId, permission) => {
    setFormData(prev => {
      const exists = prev.document_access.find(r => r.job_position_id === positionId);
      let newRules;
      if (exists) {
        if (permission === 'remove') {
          return { ...prev, document_access: prev.document_access.filter(r => r.job_position_id !== positionId) };
        }
        newRules = prev.document_access.map(r => r.job_position_id === positionId ? { ...r, [permission]: !r[permission] } : r);
      } else {
        newRules = [...prev.document_access, {
          job_position_id: positionId,
          can_view: true,
          can_download: permission === 'can_download',
          can_edit: permission === 'can_edit'
        }];
      }
      return { ...prev, document_access: newRules };
    });
  };

  const handleApprove = async (docId) => {
    if (!canApprove) return;
    try {
      await api.post(`/documents/${docId}/approve`);
      fetchDocuments();
    } catch (error) {
      console.error('Failed to approve document:', error);
    }
  };

  const handleUploadVersion = async () => {
    if (!canUpdate || !selectedDocument) return;
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      if (uploadSourceFile) {
        formData.append('source_file', uploadSourceFile); // [NEW] Append source file
      }
      formData.append('change_summary', uploadChangeSummary);
      if (uploadVersionNumber) {
        formData.append('version_number', uploadVersionNumber);
      }

      await api.post(`/documents/${selectedDocument.id}/versions`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setOpenVersionDialog(false);
      setUploadFile(null);
      setUploadSourceFile(null); // [NEW] Reset source file
      setUploadChangeSummary('');
      setUploadVersionNumber('');
      fetchDocuments();
    } catch (error) {
      console.error('Failed to upload version:', error);
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to upload version', severity: 'error' });
    }
  };

  const handleDownload = async (docId, versionNumber = null, type = 'file') => {
    try {
      const url = `/documents/${docId}/download${versionNumber ? `/${versionNumber}` : ''}${type === 'source' ? '?type=source' : ''}`;
      const response = await api.get(url, {
        responseType: 'blob'
      });

      // Get MIME type from response headers
      const mimeType = response.headers['content-type'] || 'application/octet-stream';

      // Create download link with correct MIME type
      const blob = new Blob([response.data], { type: mimeType });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;

      // Try to get filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = null;

      if (contentDisposition) {
        // Try to extract filename from Content-Disposition header
        // Supports both: filename="file.xlsx" and filename*=UTF-8''file.xlsx
        const filenameMatch = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
          // Decode URI component if needed
          try {
            filename = decodeURIComponent(filename);
          } catch (e) {
            // If decoding fails, use as-is
          }
        }
      }

      // Fallback: generate filename based on MIME type if header parsing failed
      if (!filename) {
        const mimeToExt = {
          'application/pdf': 'pdf',
          'text/csv': 'csv',
          'application/csv': 'csv',
          'text/tab-separated-values': 'tsv',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
          'application/vnd.ms-excel': 'xls',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
          'application/msword': 'doc',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
          'application/vnd.ms-powerpoint': 'ppt',
          'text/plain': 'txt',
          'application/rtf': 'rtf',
          'image/png': 'png',
          'image/jpeg': 'jpg',
          'image/gif': 'gif',
          'image/webp': 'webp'
        };

        const ext = mimeToExt[mimeType] || 'bin';
        filename = `document-${docId}${versionNumber ? `-v${versionNumber}` : ''}.${ext}`;
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Failed to download document:', error);
      setSnackbar({ open: true, message: 'Failed to download file', severity: 'error' });
    }
  };

  const handleView = async (docId, versionNumber = null) => {
    try {
      setIsViewLoading(true);
      const url = `/documents/${docId}/download${versionNumber ? `/${versionNumber}` : ''}?inline=true`;
      const response = await api.get(url, {
        responseType: 'blob'
      });

      const mimeType = response.headers['content-type'] || 'application/pdf';
      const blob = new Blob([response.data], { type: mimeType });
      const fileUrl = window.URL.createObjectURL(blob);

      // Find the document to get the title and filename
      const doc = documents.find(d => d.id === docId) || selectedDocument;
      const latestVersion = doc?.versions?.[0];
      const fileName = latestVersion?.file_name || doc?.title || 'Document Preview';

      setViewFileData({
        url: fileUrl,
        title: fileName,
        mimeType: mimeType,
        isPdf: mimeType === 'application/pdf',
        isImage: mimeType.startsWith('image/'),
        docId: docId,
        versionNumber: versionNumber
      });
      setOpenViewDialog(true);
    } catch (error) {
      console.error('Failed to view document:', error);
      setSnackbar({ open: true, message: 'Failed to view file. Please ensure the file exists and you have permission.', severity: 'error' });
    } finally {
      setIsViewLoading(false);
    }
  };

  const handleThumbnailFetch = async (doc, versionNumber = null) => {
    const url = `/documents/${doc.id}/download${versionNumber ? `/${versionNumber}` : ''}?inline=true`;
    const response = await api.get(url, { responseType: 'blob' });
    return response.data;
  };

  const handleOpenReview = (doc, action) => {
    setSelectedDocument(doc);
    setReviewAction(action);
    setReviewNotes('');
    if (action === 'submit') {
      const due = doc.review_due_at ? dayjs(doc.review_due_at) : dayjs().add(7, 'day');
      setReviewDueDate(due.format('YYYY-MM-DD'));
    } else {
      setReviewDueDate(dayjs().format('YYYY-MM-DD'));
    }
    setOpenReviewDialog(true);
  };

  const handleReviewActionSubmit = async (decision) => {
    if (!selectedDocument || !reviewAction) return;
    try {
      if (reviewAction === 'submit') {
        const reviewerId = selectedDocument.reviewed_by || user?.id;
        await api.post(`/documents/${selectedDocument.id}/submit-review`, {
          review_due_at: reviewDueDate,
          comments: reviewNotes || undefined,
          reviewer_id: reviewerId || undefined
        });
      } else if (reviewAction === 'complete') {
        await api.post(`/documents/${selectedDocument.id}/complete-review`, {
          decision: decision || 'approve',
          notes: reviewNotes || undefined
        });
      }
      setOpenReviewDialog(false);
      setSelectedDocument(null);
      setReviewAction(null);
      setReviewNotes('');
      fetchDocuments();
    } catch (error) {
      console.error('Failed to update review state:', error);
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to update review state', severity: 'error' });
    }
  };

  const handleOpenWithdraw = (doc) => {
    if (!canApprove) return;
    setSelectedDocument(doc);
    setWithdrawReason('');
    setOpenWithdrawDialog(true);
  };

  const handleConfirmWithdraw = async () => {
    if (!selectedDocument) return;
    try {
      await api.post(`/documents/${selectedDocument.id}/withdraw`, {
        reason: withdrawReason || undefined
      });
      setOpenWithdrawDialog(false);
      setSelectedDocument(null);
      setWithdrawReason('');
      fetchDocuments();
    } catch (error) {
      console.error('Failed to withdraw document:', error);
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to withdraw document', severity: 'error' });
    }
  };

  const handleOpenWorkflow = (doc) => {
    setSelectedDocument(doc);
    setShowWorkflowHistory(true);
  };

  const getStatusConfig = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.draft;

  const getCategoryLabel = (category) => {
    const labels = {
      policy: 'Policies',
      procedure: 'Procedures',
      work_instruction: 'Work Instructions',
      record: 'Records',
      form: 'Forms',
    };
    return labels[category] || category.charAt(0).toUpperCase() + category.slice(1);
  };

  // Share State
  const [shareDialog, setShareDialog] = useState({
    open: false,
    docId: null,
    docTitle: '',
    password: '',
    days: 7,
    generatedLink: null,
    loading: false
  });
  const [showSharePassword, setShowSharePassword] = useState(false);

  const handleShareOpen = (doc) => {
    setShareDialog({
      open: true,
      docId: doc.id,
      docTitle: doc.title,
      password: '',
      days: 7,
      generatedLink: null,
      loading: false
    });
  };

  const handleShareClose = () => {
    setShareDialog({ ...shareDialog, open: false });
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const newSelected = new Set(filteredDocuments.map((n) => n.id));
      setSelectedDocIds(newSelected);
    } else {
      setSelectedDocIds(new Set());
    }
  };

  const handleSelectClick = (id) => {
    const newSelected = new Set(selectedDocIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedDocIds(newSelected);
  };

  const handleBulkAccessSubmit = async () => {
    try {
      const docIds = Array.from(selectedDocIds);
      const accessLevel = bulkAccessSettings.access_level;
      const rules = bulkAccessSettings.access_rules.map(r => ({
        job_position_id: r.job_position_id,
        can_view: r.can_view,
        can_download: r.can_download,
        can_edit: r.can_edit
      }));

      // Update each document
      await Promise.all(docIds.map(docId =>
        api.put(`/documents/${docId}`, {
          access_level: accessLevel,
          document_access: accessLevel === 'restricted' ? rules : []
        })
      ));

      fetchDocuments();
      setSelectedDocIds(new Set());
      setShowBulkAccessDialog(false);
      setBulkAccessSettings({ access_level: 'organization_wide', selected_process_ids: [], access_rules: [] });
      setSnackbar({ open: true, message: `Access updated for ${docIds.length} documents`, severity: 'success' });
    } catch (error) {
      console.error('Failed to update bulk access:', error);
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to update bulk access', severity: 'error' });
    }
  };

  const handleCreateShareLink = async () => {
    try {
      setShareDialog(prev => ({ ...prev, loading: true }));
      const response = await api.post(`/documents/${shareDialog.docId}/share`, {
        days: shareDialog.days,
        password: shareDialog.password || undefined
      });
      setShareDialog(prev => ({
        ...prev,
        loading: false,
        generatedLink: response.data.share_link
      }));
    } catch (err) {
      console.error('Failed to create share link:', err);
      // Fallback if API fails or backend not ready
      setShareDialog(prev => ({ ...prev, loading: false }));
      const errorMsg = err.response?.data?.error || 'Failed to generate share link. Please try again.';
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: 'Link copied to clipboard!', severity: 'success' });
  };

  // Delete Document Functions
  const handleDeleteClick = (doc) => {
    // Check permissions
    const isAdmin = hasPermission('documents.delete');
    const isOwner = doc.created_by === user?.id;

    if (!isAdmin && !isOwner) {
      setSnackbar({ open: true, message: 'You do not have permission to delete this document.', severity: 'warning' });
      return;
    }

    if (doc.status === 'approved' && !isAdmin) {
      setSnackbar({ open: true, message: 'Cannot delete approved documents. Please contact an administrator.', severity: 'warning' });
      return;
    }

    setDeleteDialog({
      open: true,
      docId: doc.id,
      docTitle: doc.title
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/documents/${deleteDialog.docId}`);
      setDeleteDialog({ open: false, docId: null, docTitle: '' });
      fetchDocuments();
      setSnackbar({ open: true, message: 'Document deleted successfully', severity: 'success' });
    } catch (error) {
      console.error('Failed to delete document:', error);
      setSnackbar({ open: true, message: error.response?.data?.error || 'Failed to delete document', severity: 'error' });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, docId: null, docTitle: '' });
  };

  const canDeleteDocument = (doc) => {
    const isAdmin = hasPermission('documents.delete');
    const isOwner = doc.created_by === user?.id;
    return isAdmin || isOwner;
  };

  const handleBulkDelete = async () => {
    try {
      const docIds = Array.from(selectedDocIds);
      const results = await Promise.allSettled(docIds.map(id => api.delete(`/documents/${id}`)));
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;
      setBulkDeleteDialog(false);
      setSelectedDocIds(new Set());
      fetchDocuments();
      if (failed > 0) {
        setSnackbar({ open: true, message: `Deleted ${succeeded} documents. ${failed} failed (may be approved or lack permission).`, severity: 'warning' });
      } else {
        setSnackbar({ open: true, message: `${succeeded} document${succeeded !== 1 ? 's' : ''} deleted successfully`, severity: 'success' });
      }
    } catch (error) {
      console.error('Bulk delete failed:', error);
      setSnackbar({ open: true, message: 'Failed to delete documents', severity: 'error' });
    }
  };

  const canCreate = hasPermission('documents.create');
  const canApprove = hasPermission('documents.approve');
  const canUpdate = hasPermission('documents.update');
  const canDownload = hasPermission('documents.download');
  const canView = hasPermission('documents.view');

  const getClassificationColor = (classification) => {
    switch (classification) {
      case 'restricted': return 'error';
      case 'confidential': return 'warning';
      case 'internal': return 'info';
      case 'public': return 'success';
      default: return 'default';
    }
  };

  // No client-side filtering for list view to avoid conflicts with backend pagination
  // except for withdrawn logic if not handled by backend completely yet
  const filteredDocuments = React.useMemo(() => {
    if (sortOrder === 'default') return documents;
    return [...documents].sort((a, b) => {
      const titleA = (a.title || '').toLowerCase();
      const titleB = (b.title || '').toLowerCase();
      return sortOrder === 'asc' ? titleA.localeCompare(titleB) : titleB.localeCompare(titleA);
    });
  }, [documents, sortOrder]);

  if (loading && documents.length === 0) {
    return <Loading />;
  }

  return (
    <Box sx={{ position: 'relative', minHeight: '100%', width: '100%', maxWidth: '100%', mx: 'auto', p: { xs: 1, md: 3 } }}>
      {/* Ambient Background */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '500px',
          background: 'radial-gradient(circle at 50% -20%, rgba(124, 58, 237, 0.15), rgba(79, 70, 229, 0.05), transparent 70%)',
          filter: 'blur(80px)',
          zIndex: 0,
          pointerEvents: 'none'
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          right: '-10%',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(56, 189, 248, 0.08), transparent 70%)',
          filter: 'blur(100px)',
          zIndex: 0,
          pointerEvents: 'none'
        }}
      />

      <Box display="flex" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={{ xs: 1, sm: 2 }} mb={{ xs: 2, md: 4 }} sx={{ position: 'relative', zIndex: 1 }}>
        <Box>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
                background: (theme) => theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #fff 0%, #cbd5e1 100%)'
                  : 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.5px',
                textShadow: (theme) => theme.palette.mode === 'dark' ? '0 2px 10px rgba(0,0,0,0.1)' : 'none'
              }}
            >
              Documents
            </Typography>
            {refreshing && (
              <Chip
                label="Syncing..."
                size="small"
                sx={{
                  bgcolor: 'rgba(56, 189, 248, 0.1)',
                  color: '#38bdf8',
                  border: '1px solid rgba(56, 189, 248, 0.2)',
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.5 },
                    '100%': { opacity: 1 }
                  }
                }}
              />
            )}
          </Box>
          {lastRefreshedAt && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', opacity: 0.8 }}>
              Last synced: {dayjs(lastRefreshedAt).format('HH:mm:ss')}
            </Typography>
          )}
        </Box>
        <Box display="flex" flexWrap="wrap" gap={2}>
          {selectedDocIds.size > 0 && hasPermission('documents.update') && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ShieldIcon />}
              onClick={() => setShowBulkAccessDialog(true)}
              sx={{
                borderColor: 'divider',
                color: 'text.primary',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05)
                }
              }}
            >
              Bulk Access ({selectedDocIds.size})
            </Button>
          )}
          {selectedDocIds.size > 0 && hasPermission('documents.delete') && (
            <Button
              variant="outlined"
              startIcon={<DeleteIcon />}
              onClick={() => setBulkDeleteDialog(true)}
              sx={{
                borderColor: 'rgba(239,68,68,0.3)',
                color: '#ef4444',
                '&:hover': {
                  borderColor: '#ef4444',
                  bgcolor: 'rgba(239,68,68,0.05)'
                }
              }}
            >
              Delete ({selectedDocIds.size})
            </Button>
          )}
          <PermissionGate permission="documents.create">
            <Button
              id="new-document-btn"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => { titleRef.current = ''; descRef.current = ''; setOpenDialog(true); }}
              sx={{
                background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                padding: { xs: '6px 16px', sm: '8px 24px' },
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '0.8rem', sm: '0.95rem' },
                border: '1px solid rgba(255,255,255,0.1)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
                  boxShadow: '0 6px 16px rgba(99, 102, 241, 0.4)',
                  transform: 'translateY(-1px)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              New Document
            </Button>
          </PermissionGate>
        </Box>
      </Box>

      {/* Filters */}
      <Paper
        id="document-filters"
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: { xs: 2, md: 3 },
          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.4)' : 'background.paper',
          backdropFilter: (theme) => theme.palette.mode === 'dark' ? 'blur(12px)' : 'none',
          border: '1px solid',
          borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'divider',
          borderRadius: 3,
          boxShadow: (theme) => theme.palette.mode === 'dark'
            ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
        }}
      >
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField
            id="doc-search"
            size="small"
            placeholder="Search documents..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            sx={{
              minWidth: { xs: '100%', sm: 200, md: 250 },
              flex: { xs: '1 1 100%', sm: '0 1 auto' },
              '& .MuiOutlinedInput-root': {
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'transparent',
                backdropFilter: (theme) => theme.palette.mode === 'dark' ? 'blur(4px)' : 'none',
                '& fieldset': { borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'divider' },
                '&:hover fieldset': { borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'primary.light' },
                '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main }
              }
            }}
          />
          <FormControl size="small" sx={{ minWidth: { xs: '45%', sm: 120, md: 150 }, flex: { xs: '1 1 45%', sm: '0 1 auto' } }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              label="Status"
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'transparent' }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="pending_approval">Pending Approval</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="withdrawn">Withdrawn</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: '45%', sm: 120, md: 150 }, flex: { xs: '1 1 45%', sm: '0 1 auto' } }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={filters.category}
              label="Category"
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'transparent' }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="policy">Policies</MenuItem>
              <MenuItem value="procedure">Procedures</MenuItem>
              <MenuItem value="work_instruction">Work Instructions</MenuItem>
              <MenuItem value="form">Forms</MenuItem>
              <MenuItem value="record">Records</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: '45%', sm: 120, md: 150 }, flex: { xs: '1 1 45%', sm: '0 1 auto' } }}>
            <InputLabel>Standard</InputLabel>
            <Select
              value={filters.document_type}
              label="Standard"
              onChange={(e) => setFilters({ ...filters, document_type: e.target.value })}
              sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'transparent' }}
            >
              <MenuItem value="all">All</MenuItem>
              {availableStandards?.map((standard) => (
                <MenuItem key={standard} value={standard}>
                  {standard.toUpperCase()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: '45%', sm: 130, md: 180 }, flex: { xs: '1 1 45%', sm: '0 1 auto' } }}>
            <InputLabel>Owner</InputLabel>
            <Select
              value={filters.owner}
              label="Owner"
              onChange={(e) => setFilters({ ...filters, owner: e.target.value })}
              sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'transparent' }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="mine">My Documents</MenuItem>
              {ownerOptions.map((owner) => (
                <MenuItem key={owner.id} value={owner.id}>
                  {owner.first_name} {owner.last_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={filters.showWithdrawn}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    showWithdrawn: e.target.checked
                  })
                }
                sx={{
                  color: 'text.secondary',
                  '&.Mui-checked': {
                    color: 'error.main',
                  },
                }}
              />
            }
            label={<Typography variant="body2" color="text.secondary">Include withdrawn</Typography>}
          />
          <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: 'divider', display: { xs: 'none', sm: 'block' } }} />
          <Box sx={{ display: 'flex', bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)', borderRadius: 2, p: 0.5 }}>
            <Tooltip title="List View">
              <IconButton
                size="small"
                onClick={() => setViewMode('list')}
                sx={{
                  bgcolor: viewMode === 'list' ? 'rgba(99, 102, 241, 0.2)' : 'transparent',
                  color: viewMode === 'list' ? 'primary.main' : 'text.secondary',
                  '&:hover': { bgcolor: viewMode === 'list' ? 'rgba(99, 102, 241, 0.3)' : 'rgba(255,255,255,0.05)' }
                }}
              >
                <ListIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Hierarchy View">
              <IconButton
                size="small"
                onClick={() => setViewMode('chart')}
                sx={{
                  bgcolor: (theme) => viewMode === 'chart' ? (theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.2)' : alpha(theme.palette.primary.main, 0.1)) : 'transparent',
                  color: viewMode === 'chart' ? 'primary.main' : 'text.secondary',
                  '&:hover': { bgcolor: (theme) => viewMode === 'chart' ? (theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.3)' : alpha(theme.palette.primary.main, 0.2)) : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)') }
                }}
              >
                <ChartIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper >

      {/* Access Denied / Restricted View would be handled by conditional rendering if user had NO access, but route handles it */}


      {
        viewMode === 'list' ? (
          <>
            {/* Tabs */}
            <Paper
              sx={{
                mb: 2,
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center',
                pr: 2,
                bgcolor: 'transparent',
                backgroundImage: 'none',
                boxShadow: 'none',
                border: 'none',
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: { xs: '0.75rem', sm: '0.9rem' },
                    color: 'text.secondary',
                    minHeight: 48,
                    minWidth: { xs: 'auto', sm: 90 },
                    px: { xs: 1, sm: 2 },
                    '&.Mui-selected': { color: 'primary.main' }
                  },
                  '& .MuiTabs-indicator': {
                    bgcolor: 'primary.main',
                    height: 3,
                    borderRadius: '3px 3px 0 0'
                  }
                }}
              >
                <Tab label={`All (${serverCounts.all})`} />
                <Tab label={`Pending Approval (${serverCounts.pendingApproval})`} />
                <Tab label={`Approved (${serverCounts.approved})`} />
              </Tabs>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FormControl size="small" sx={{ minWidth: { xs: 100, sm: 130 } }}>
                  <Select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    displayEmpty
                    startAdornment={<SortByAlphaIcon sx={{ mr: 0.5, fontSize: 18, color: 'text.secondary' }} />}
                    sx={{
                      fontSize: '0.8rem',
                      color: 'text.secondary',
                      '& .MuiSelect-select': { py: 0.75 },
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    }}
                  >
                    <MenuItem value="default">Default</MenuItem>
                    <MenuItem value="asc">A → Z</MenuItem>
                    <MenuItem value="desc">Z → A</MenuItem>
                  </Select>
                </FormControl>
              <Tooltip title="Refresh List">
                <IconButton onClick={fetchDocuments} size="small" disabled={refreshing} sx={{ color: 'text.secondary' }}>
                  <RefreshIcon
                    sx={{
                      animation: refreshing ? 'spin 1s linear infinite' : 'none',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      },
                    }}
                  />
                </IconButton>
              </Tooltip>
              </Box>
            </Paper>

            {/* Documents Table */}
            <TableContainer
              component={Paper}
              id="documents-table"
              sx={{
                bgcolor: (theme) => theme.palette.mode === 'dark' ? '#1e293b' : 'background.paper',
                backdropFilter: (theme) => theme.palette.mode === 'dark' ? 'blur(12px)' : 'none',
                border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : theme.palette.divider}`,
                borderRadius: 3,
                boxShadow: (theme) => theme.palette.mode === 'dark'
                  ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                  : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                '&::-webkit-scrollbar': { height: 8 },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                  borderRadius: 4
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)',
                  borderRadius: 4,
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.25)'
                  }
                }
              }}
            >
              <Table size="small" sx={{ minWidth: 1000, borderCollapse: 'separate', tableLayout: 'auto' }}>
                <TableHead sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.02)' }}>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedDocIds.size > 0 && selectedDocIds.size < filteredDocuments.length}
                        checked={filteredDocuments.length > 0 && selectedDocIds.size === filteredDocuments.length}
                        onChange={handleSelectAll}
                        inputProps={{ 'aria-label': 'select all documents' }}
                        sx={{ color: 'text.secondary' }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 600, whiteSpace: 'nowrap' }}>Title</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 600, whiteSpace: 'nowrap' }}>Version</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 600, whiteSpace: 'nowrap' }}>Status</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 600, whiteSpace: 'nowrap' }}>Approved By</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 600, whiteSpace: 'nowrap' }}>Owning Process</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 600, whiteSpace: 'nowrap' }}>Last Updated</TableCell>
                    <TableCell sx={{ color: 'text.secondary', fontWeight: 600, whiteSpace: 'nowrap' }}>Next Update</TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDocuments.map((doc) => (
                    <TableRow
                      key={doc.id}
                      hover
                      sx={{
                        transition: 'all 0.2s ease',
                        '&:hover': { bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.01)' },
                        ...(updatedDocIds.has(doc.id) && {
                          backgroundColor: (theme) => alpha(theme.palette.success.main, 0.1),
                          borderLeft: (theme) => `4px solid ${theme.palette.success.main}`
                        })
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedDocIds.has(doc.id)}
                          onChange={(event) => handleSelectClick(doc.id)}
                          inputProps={{ 'aria-labelledby': `enhanced-table-checkbox-${doc.id}` }}
                          sx={{ color: 'text.secondary' }}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300, wordBreak: 'break-word' }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight="600" color="text.primary" sx={{ wordBreak: 'break-word' }}>
                              {doc.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {doc.description}
                            </Typography>
                            {doc.is_confidential && (
                              <Chip
                                label="CONFIDENTIAL"
                                size="small"
                                color="error"
                                variant="outlined"
                                sx={{ ml: 0, mt: 0.5, height: 18, fontSize: '0.6rem', fontWeight: 'bold' }}
                              />
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" fontWeight="500">v{doc.current_version}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label={getStatusConfig(doc.status).label}
                            color={getStatusConfig(doc.status).color}
                            size="small"
                            variant={getStatusConfig(doc.status).color === 'default' ? 'outlined' : 'filled'}
                            sx={{ fontWeight: 600 }}
                          />
                          {updatedDocIds.has(doc.id) && (
                            <Chip
                              label="UPDATED"
                              color="success"
                              size="small"
                              variant="filled"
                              sx={{
                                height: 20,
                                fontSize: '0.65rem',
                                fontWeight: 'bold',
                                animation: 'pulse-green 2s infinite',
                                '@keyframes pulse-green': {
                                  '0%': { boxShadow: '0 0 0 0px rgba(16, 185, 129, 0.4)' },
                                  '70%': { boxShadow: '0 0 0 6px rgba(16, 185, 129, 0)' },
                                  '100%': { boxShadow: '0 0 0 0px rgba(16, 185, 129, 0)' }
                                }
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {doc.approved_by_user ? (
                          <Stack spacing={0.3}>
                            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                              {doc.approved_by_user.first_name} {doc.approved_by_user.last_name}
                            </Typography>
                            {doc.approved_by_user.job_position?.title && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                {doc.approved_by_user.job_position?.title}
                              </Typography>
                            )}
                            {doc.approved_at && (
                              <Typography variant="caption" color="success.main" sx={{ fontSize: '0.7rem' }}>
                                {formatDate(doc.approved_at)}
                              </Typography>
                            )}
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" fontWeight="medium" color="text.primary" sx={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                            {doc.process ? doc.process.title : (doc.created_by_user
                              ? `${doc.created_by_user.first_name} ${doc.created_by_user.last_name}`
                              : '—')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {doc.process ? 'Process Owner' : 'Individual Owner'}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{formatDate(doc.updated_at)}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1} sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            {formatDate(doc.next_update_at)}
                          </Typography>
                          <PermissionGate permission="documents.update">
                            <IconButton
                              size="small"
                              onClick={(e) => handleQuickUpdateDate(e, doc)}
                              sx={{
                                padding: 0.5,
                                opacity: 0.6,
                                '&:hover': { opacity: 1, color: 'primary.main', bgcolor: 'rgba(99, 102, 241, 0.1)' }
                              }}
                            >
                              <EditIcon sx={{ fontSize: '0.85rem' }} />
                            </IconButton>
                          </PermissionGate>
                        </Box>
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          whiteSpace: 'nowrap',
                          py: 0.5
                        }}
                      >
                        <Box display="flex" justifyContent="flex-end" alignItems="center" gap={0.5}>
                          {doc.status === 'pending_approval' && canApprove && (
                            <Tooltip title="Approve">
                              <IconButton
                                size="small"
                                onClick={() => handleApprove(doc.id)}
                                sx={{ color: 'success.main', '&:hover': { bgcolor: 'rgba(76, 175, 80, 0.1)' } }}
                              >
                                <ApproveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <IconButton
                            size="small"
                            onClick={(e) => { setActionMenuAnchor(e.currentTarget); setActionMenuDoc(doc); }}
                            sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'rgba(99, 102, 241, 0.1)' } }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalDocs}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[8, 16, 24, 48]}
              sx={{ borderTop: '1px solid #e0e0e0' }}
            />
          </>
        ) : (
          <DocumentHierarchyFlow
            processes={processes}
            departments={departments}
            documents={filteredDocuments}
            onLink={handleLinkDocument}
            onUnlink={handleUnlinkDocument}
          />
        )
      }

      {/* Actions Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={() => { setActionMenuAnchor(null); setActionMenuDoc(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 200, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.12)' } }}
      >
        {/* Approve */}
        {canApprove && actionMenuDoc?.status === 'pending_approval' && (
          <MenuItem onClick={() => { handleApprove(actionMenuDoc.id); setActionMenuAnchor(null); setActionMenuDoc(null); }} sx={{ color: 'success.main' }}>
            <ListItemIcon><ApproveIcon fontSize="small" sx={{ color: 'success.main' }} /></ListItemIcon>
            <ListItemText>Approve</ListItemText>
          </MenuItem>
        )}

        {/* Submit for Review */}
        {canUpdate && actionMenuDoc?.status === 'draft' && (
          <MenuItem onClick={() => { handleOpenReview(actionMenuDoc, 'submit'); setActionMenuAnchor(null); setActionMenuDoc(null); }}>
            <ListItemIcon><UploadIcon fontSize="small" sx={{ color: 'info.main' }} /></ListItemIcon>
            <ListItemText>Submit for Review</ListItemText>
          </MenuItem>
        )}

        {/* Workflow Timeline */}
        <MenuItem onClick={() => { handleOpenWorkflow(actionMenuDoc); setActionMenuAnchor(null); setActionMenuDoc(null); }}>
          <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Workflow Timeline</ListItemText>
        </MenuItem>

        {/* Preview */}
        {canView && (
          <MenuItem onClick={() => { handleView(actionMenuDoc.id); setActionMenuAnchor(null); setActionMenuDoc(null); }}>
            <ListItemIcon><ViewIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Preview</ListItemText>
          </MenuItem>
        )}

        {/* Download */}
        {canDownload && (
          <MenuItem onClick={() => { handleDownload(actionMenuDoc.id); setActionMenuAnchor(null); setActionMenuDoc(null); }}>
            <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Download</ListItemText>
          </MenuItem>
        )}

        {/* Secure Share */}
        {canDownload && (
          <MenuItem onClick={() => { handleShareOpen(actionMenuDoc); setActionMenuAnchor(null); setActionMenuDoc(null); }}>
            <ListItemIcon><ShareIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Secure Share</ListItemText>
          </MenuItem>
        )}

        {/* Edit Details */}
        {canUpdate && actionMenuDoc?.status !== 'withdrawn' && (
          <MenuItem onClick={() => { handleEditClick(actionMenuDoc); setActionMenuAnchor(null); setActionMenuDoc(null); }}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Edit Details</ListItemText>
          </MenuItem>
        )}

        {/* Upload New Version */}
        {canUpdate && actionMenuDoc?.status !== 'withdrawn' && (
          <MenuItem onClick={() => { setSelectedDocument(actionMenuDoc); setOpenVersionDialog(true); setActionMenuAnchor(null); setActionMenuDoc(null); }}>
            <ListItemIcon><UploadIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Upload Version</ListItemText>
          </MenuItem>
        )}

        {/* Complete Review */}
        {canApprove && actionMenuDoc?.status === 'in_review' && (
          <MenuItem onClick={() => { handleOpenReview(actionMenuDoc, 'complete'); setActionMenuAnchor(null); setActionMenuDoc(null); }}>
            <ListItemIcon><CompleteReviewIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Complete Review</ListItemText>
          </MenuItem>
        )}

        {/* Withdraw */}
        {canApprove && actionMenuDoc?.status === 'approved' && (
          <MenuItem onClick={() => { handleOpenWithdraw(actionMenuDoc); setActionMenuAnchor(null); setActionMenuDoc(null); }}>
            <ListItemIcon><WithdrawIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Withdraw</ListItemText>
          </MenuItem>
        )}

        {/* Delete */}
        {actionMenuDoc && canDeleteDocument(actionMenuDoc) && (
          <MenuItem onClick={() => { handleDeleteClick(actionMenuDoc); setActionMenuAnchor(null); setActionMenuDoc(null); }} sx={{ color: 'error.main' }}>
            <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Create Document Dialog */}
      <Dialog open={openDialog} onClose={() => {
        setOpenDialog(false);
        setEditingDoc(null);
        setFormData({
          title: '',
          description: '',
          category: 'policy',
          classification: 'internal',
          document_type: defaultStandard,
          process_id: '',
          document_code: '',
          access_level: 'organization_wide',
          is_confidential: false,
          require_acknowledgment: false,
          document_access: [],
          temp_selected_processes: [],
          next_update_at: dayjs().add(1, 'year').format('YYYY-MM-DD'),
          version_number: '1.0'
        });
        setUploadFile(null);
        setUploadSourceFile(null);
      }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#0f172a',
            backgroundImage: 'radial-gradient(ellipse at 20% 0%, rgba(99, 102, 241, 0.08) 0%, transparent 50%), radial-gradient(ellipse at 80% 100%, rgba(139, 92, 246, 0.06) 0%, transparent 50%)',
            backdropFilter: 'blur(40px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            boxShadow: '0 32px 64px -12px rgba(0, 0, 0, 0.7), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
            borderRadius: 4,
            color: 'white',
            overflow: 'hidden'
          }
        }}
      >
        {/* Header */}
        <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: '1px solid rgba(255,255,255,0.06)', background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(139, 92, 246, 0.03) 100%)' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box sx={{ width: 40, height: 40, borderRadius: 2, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)' }}>
                <DescriptionIcon sx={{ color: 'white', fontSize: 20 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, background: 'linear-gradient(135deg, #e0e7ff, #c7d2fe)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', lineHeight: 1.2 }}>
                  {editingDoc ? 'Edit Document' : 'Create New Document'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                  {editingDoc ? 'Update document details and access' : 'Set up a new controlled document'}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={() => setOpenDialog(false)} size="small" sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.08)' } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ p: 3, '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 3 } }}>

          {/* Section: Document Info */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.35)', letterSpacing: 2, fontSize: 10, fontWeight: 700, mb: 2, display: 'block' }}>Document Information</Typography>

            {/* Row: Type + Code */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={5}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mb: 0.5, display: 'block', fontWeight: 600 }}>Type</Typography>
                <Select
                  fullWidth
                  size="small"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.04)',
                    color: 'white',
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.1)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                    '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.5)' }
                  }}
                  MenuProps={{ PaperProps: { sx: { bgcolor: '#1e293b', color: 'white' } } }}
                >
                  <MenuItem value="policy">Policy</MenuItem>
                  <MenuItem value="procedure">Procedure</MenuItem>
                  <MenuItem value="work_instruction">Work Instruction</MenuItem>
                  <MenuItem value="form">Form</MenuItem>
                  <MenuItem value="record">Record</MenuItem>
                </Select>
              </Grid>
              <Grid item xs={12} sm={7}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mb: 0.5, display: 'block', fontWeight: 600 }}>Document Code</Typography>
                <Box display="flex" gap={1}>
                  <TextField
                    fullWidth
                    size="small"
                    value={formData.document_code}
                    onChange={(e) => setFormData({ ...formData, document_code: e.target.value })}
                    placeholder="e.g. PRO-4783"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'rgba(255,255,255,0.04)', color: 'white', borderRadius: 2,
                        '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                        '&.Mui-focused fieldset': { borderColor: '#6366f1' },
                      }
                    }}
                  />
                  <Button
                    size="small"
                    onClick={generateDocumentCode}
                    sx={{
                      minWidth: 'auto', px: 1.5, borderRadius: 2,
                      bgcolor: 'rgba(99, 102, 241, 0.1)', color: '#818cf8', border: '1px solid rgba(99, 102, 241, 0.2)',
                      '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.2)' }
                    }}
                  >
                    <RefreshIcon fontSize="small" />
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {/* Document Name */}
            <TextField
              fullWidth
              required
              size="small"
              label="Document Name"
              key={editingDoc ? `title-${editingDoc.id}` : `title-new-${openDialog}`}
              defaultValue={formData.title}
              onChange={(e) => { titleRef.current = e.target.value; }}
              onBlur={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="e.g. Quality Policy"
              sx={{
                mt: 2,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255,255,255,0.04)', color: 'white', borderRadius: 2,
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                  '&.Mui-focused fieldset': { borderColor: '#6366f1' },
                },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#818cf8' },
              }}
            />

            {/* Row: Owner + Next Update */}
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Owner"
                  value={user ? `${user.first_name} ${user.last_name}` : ''}
                  InputProps={{ readOnly: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'rgba(255,255,255,0.02)', color: 'rgba(255,255,255,0.6)', borderRadius: 2,
                      '& fieldset': { borderColor: 'rgba(255,255,255,0.06)' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.4)' },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Next Update"
                  value={formData.next_update_at}
                  onChange={(e) => setFormData({ ...formData, next_update_at: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'rgba(255,255,255,0.04)', color: 'white', borderRadius: 2,
                      '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                      '&.Mui-focused fieldset': { borderColor: '#6366f1' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
                    '& input::-webkit-calendar-picker-indicator': { filter: 'invert(0.7)' },
                  }}
                />
              </Grid>
            </Grid>

            {/* Description */}
            <TextField
              fullWidth
              size="small"
              multiline
              rows={2}
              label="Description"
              key={editingDoc ? `desc-${editingDoc.id}` : `desc-new-${openDialog}`}
              defaultValue={formData.description}
              onChange={(e) => { descRef.current = e.target.value; }}
              onBlur={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description of this document..."
              sx={{
                mt: 2,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255,255,255,0.04)', color: 'white', borderRadius: 2,
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                  '&.Mui-focused fieldset': { borderColor: '#6366f1' },
                },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
                '& .MuiInputLabel-root.Mui-focused': { color: '#818cf8' },
              }}
            />
          </Box>

          {/* Section: Access Control */}
          <Box sx={{
            p: 2.5, borderRadius: 3,
            bgcolor: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.06)',
            mb: 3
          }}>
            <Box display="flex" alignItems="center" gap={1.5} mb={2}>
              <Box sx={{ width: 32, height: 32, borderRadius: 1.5, bgcolor: 'rgba(99, 102, 241, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ShieldIcon sx={{ color: '#818cf8', fontSize: 18 }} />
              </Box>
              <Box>
                <Typography variant="subtitle2" fontWeight={700} color="white">Access Control</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>Define who can view, download, and edit this document</Typography>
              </Box>
            </Box>

            {/* Access Level Toggle */}
            <Box display="flex" gap={1.5} mb={2}>
              {[
                { value: 'organization_wide', label: 'Organization-Wide', icon: <PublicIcon sx={{ fontSize: 16 }} />, desc: 'All employees can access', color: '#22c55e' },
                { value: 'restricted', label: 'Restricted', icon: <LockIcon sx={{ fontSize: 16 }} />, desc: 'Only assigned processes / positions', color: '#f59e0b' }
              ].map(opt => (
                <Box
                  key={opt.value}
                  onClick={() => {
                    if (opt.value === 'organization_wide') {
                      setFormData({ ...formData, access_level: 'organization_wide', document_access: [], temp_selected_processes: [] });
                    } else {
                      setFormData({ ...formData, access_level: 'restricted' });
                    }
                  }}
                  sx={{
                    flex: 1, p: 1.5, borderRadius: 2, cursor: 'pointer',
                    border: `2px solid ${formData.access_level === opt.value ? opt.color : 'rgba(255,255,255,0.06)'}`,
                    bgcolor: formData.access_level === opt.value ? alpha(opt.color, 0.08) : 'transparent',
                    transition: 'all 0.2s ease',
                    '&:hover': { borderColor: alpha(opt.color, 0.4) }
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={0.3}>
                    <Box sx={{ color: formData.access_level === opt.value ? opt.color : 'rgba(255,255,255,0.3)' }}>{opt.icon}</Box>
                    <Typography variant="body2" fontWeight={700} sx={{ color: formData.access_level === opt.value ? 'white' : 'rgba(255,255,255,0.4)', fontSize: 13 }}>{opt.label}</Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 10 }}>{opt.desc}</Typography>
                </Box>
              ))}
            </Box>

            {/* Process & Position Selection — only when restricted */}
            {formData.access_level === 'restricted' && (<>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mb: 0.5, display: 'block', fontWeight: 600 }}>Link to Process</Typography>
              <Autocomplete
                multiple
                options={processes || []}
                getOptionLabel={(option) => option?.title || ''}
                value={formData.temp_selected_processes || []}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                onChange={(event, newValue) => {
                  const primary = newValue.length > 0 ? newValue[0].id : '';
                  let newAccessRules = [...(formData.document_access || [])];

                  const ensureAccess = (posId) => {
                    if (!posId) return;
                    if (!newAccessRules.find(r => r.job_position_id === posId)) {
                      newAccessRules.push({ job_position_id: posId, can_view: true, can_download: false, can_edit: false });
                    }
                  };

                  newValue.forEach(p => {
                    if (p.owner_position_id) ensureAccess(p.owner_position_id);
                    if (p.owner_position_ids) p.owner_position_ids.forEach(id => ensureAccess(id));
                    if (p.backup_owner_position_ids) p.backup_owner_position_ids.forEach(id => ensureAccess(id));
                    const linkedPositions = jobPositions.filter(jp => jp.process_id === p.id);
                    linkedPositions.forEach(jp => ensureAccess(jp.id));
                  });

                  setFormData({
                    ...formData,
                    process_id: primary,
                    access_level: 'restricted',
                    temp_selected_processes: newValue,
                    document_access: newAccessRules
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Search and add processes..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'rgba(255,255,255,0.04)', color: 'white', borderRadius: 2,
                        '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                        '&.Mui-focused fieldset': { borderColor: '#6366f1' },
                      },
                      '& .MuiChip-root': { bgcolor: 'rgba(99, 102, 241, 0.15)', color: '#c7d2fe', border: '1px solid rgba(99, 102, 241, 0.25)', borderRadius: 1.5, fontWeight: 600, fontSize: 12 }
                    }}
                  />
                )}
                PaperComponent={({ children }) => (
                  <Paper sx={{ bgcolor: '#1e293b', color: 'white', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, mt: 0.5 }}>{children}</Paper>
                )}
              />
            </Box>

            {/* Render Permissions for Selected Processes */}
            {(formData.temp_selected_processes || []).map(process => {
              const relevantPositions = jobPositions.filter(jp =>
                jp.id === process.owner_position_id ||
                jp.process_id === process.id ||
                (process.owner_position_ids && process.owner_position_ids.includes(jp.id)) ||
                (process.backup_owner_position_ids && process.backup_owner_position_ids.includes(jp.id))
              );

              return (
                <Box key={process.id} sx={{ mb: 2, borderRadius: 2, border: '1px solid rgba(99, 102, 241, 0.15)', overflow: 'hidden' }}>
                  <Box sx={{ px: 2, py: 1, bgcolor: 'rgba(99, 102, 241, 0.08)', borderBottom: '1px solid rgba(99, 102, 241, 0.12)', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ChartIcon sx={{ fontSize: 15, color: '#818cf8' }} />
                    <Typography variant="caption" sx={{ color: '#a5b4fc', fontWeight: 700, letterSpacing: 0.5 }}>
                      {process.title}
                    </Typography>
                    <Chip label={`${relevantPositions.length} positions`} size="small" sx={{ ml: 'auto', height: 20, fontSize: 10, bgcolor: 'rgba(99, 102, 241, 0.15)', color: '#c7d2fe' }} />
                  </Box>
                  <Stack divider={<Divider sx={{ borderColor: 'rgba(255,255,255,0.04)' }} />}>
                    {relevantPositions.map(pos => {
                      const rule = formData.document_access?.find(r => r.job_position_id === pos.id);
                      const hasView = rule?.can_view;
                      const assignedUsers = users.filter(u => u.job_position_id === pos.id);
                      const userNames = assignedUsers.length > 0 ? assignedUsers.map(u => `${u.first_name} ${u.last_name}`).join(', ') : '';

                      return (
                        <Box key={pos.id} display="flex" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.2 }}>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, fontSize: 13 }}>{pos.title}</Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>{userNames}</Typography>
                          </Box>
                          <Box display="flex" gap={0.5}>
                            {['can_view', 'can_download', 'can_edit'].map(perm => {
                              const label = perm.replace('can_', '').charAt(0).toUpperCase() + perm.replace('can_', '').slice(1);
                              const isChecked = !!rule?.[perm];
                              const isDisabled = perm !== 'can_view' && !hasView;
                              return (
                                <Chip
                                  key={perm}
                                  label={label}
                                  size="small"
                                  onClick={() => !isDisabled && handleAccessChange(pos.id, perm)}
                                  sx={{
                                    height: 26, fontSize: 11, fontWeight: 600, cursor: isDisabled ? 'default' : 'pointer',
                                    bgcolor: isChecked ? (perm === 'can_view' ? 'rgba(52, 211, 153, 0.15)' : perm === 'can_download' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(251, 191, 36, 0.15)') : 'rgba(255,255,255,0.04)',
                                    color: isChecked ? (perm === 'can_view' ? '#34d399' : perm === 'can_download' ? '#60a5fa' : '#fbbf24') : 'rgba(255,255,255,0.25)',
                                    border: `1px solid ${isChecked ? (perm === 'can_view' ? 'rgba(52, 211, 153, 0.3)' : perm === 'can_download' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(251, 191, 36, 0.3)') : 'rgba(255,255,255,0.06)'}`,
                                    opacity: isDisabled ? 0.3 : 1,
                                    transition: 'all 0.15s ease',
                                    '&:hover': !isDisabled ? { transform: 'scale(1.05)' } : {},
                                  }}
                                />
                              );
                            })}
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              );
            })}

            {/* Additional Job Positions */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mb: 0.5, display: 'block', fontWeight: 600 }}>Additional Job Positions</Typography>
              <Autocomplete
                multiple
                options={jobPositions.filter(jp =>
                  !formData.document_access?.some(r => r.job_position_id === jp.id)
                )}
                getOptionLabel={(option) => {
                  const assignedUsers = users.filter(u => u.job_position_id === option.id);
                  const userNames = assignedUsers.length > 0 ? assignedUsers.map(u => `${u.first_name} ${u.last_name}`).join(', ') : '';
                  return userNames ? `${option.title} (${userNames})` : option.title;
                }}
                onChange={(event, newValue) => {
                  const newAccessRules = [...(formData.document_access || [])];
                  newValue.forEach(pos => {
                    if (!newAccessRules.find(r => r.job_position_id === pos.id)) {
                      newAccessRules.push({ job_position_id: pos.id, can_view: true, can_download: false, can_edit: false });
                    }
                  });
                  setFormData({ ...formData, access_level: 'restricted', document_access: newAccessRules });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Add specific job positions..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'rgba(255,255,255,0.04)', color: 'white', borderRadius: 2,
                        '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                        '&.Mui-focused fieldset': { borderColor: '#6366f1' },
                      },
                      '& .MuiChip-root': { bgcolor: 'rgba(139, 92, 246, 0.15)', color: '#c4b5fd', border: '1px solid rgba(139, 92, 246, 0.25)', borderRadius: 1.5, fontWeight: 600, fontSize: 12 }
                    }}
                  />
                )}
                PaperComponent={({ children }) => (
                  <Paper sx={{ bgcolor: '#1e293b', color: 'white', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2, mt: 0.5 }}>{children}</Paper>
                )}
              />

              {/* Render Additional Rules (not in selected processes) */}
              <Stack spacing={0} sx={{ mt: 1 }}>
                {formData.document_access?.filter(rule => {
                  const selectedProcessIds = formData.temp_selected_processes?.map(p => p.id) || (formData.process_id ? [formData.process_id] : []);
                  const position = jobPositions.find(p => p.id === rule.job_position_id);
                  if (!position) return false;
                  const isProcessLinked = selectedProcessIds.some(pid => {
                    const proc = processes.find(p => p.id === pid);
                    return position.process_id === pid ||
                      proc?.owner_position_id === position.id ||
                      (proc?.owner_position_ids && proc.owner_position_ids.includes(position.id)) ||
                      (proc?.backup_owner_position_ids && proc.backup_owner_position_ids.includes(position.id));
                  });
                  return !isProcessLinked;
                }).map(rule => {
                  const pos = jobPositions.find(p => p.id === rule.job_position_id);
                  if (!pos) return null;
                  const hasView = rule.can_view;
                  const assignedUsers = users.filter(u => u.job_position_id === pos.id);
                  const userNames = assignedUsers.length > 0 ? assignedUsers.map(u => `${u.first_name} ${u.last_name}`).join(', ') : '';

                  return (
                    <Box key={pos.id} display="flex" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.2, borderRadius: 1.5, bgcolor: 'rgba(139, 92, 246, 0.05)', border: '1px solid rgba(139, 92, 246, 0.1)', mb: 0.5 }}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, fontSize: 13 }}>{pos.title}</Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>{userNames}</Typography>
                      </Box>
                      <Box display="flex" gap={0.5} alignItems="center">
                        {['can_view', 'can_download', 'can_edit'].map(perm => {
                          const label = perm.replace('can_', '').charAt(0).toUpperCase() + perm.replace('can_', '').slice(1);
                          const isChecked = !!rule[perm];
                          const isDisabled = perm !== 'can_view' && !hasView;
                          return (
                            <Chip
                              key={perm}
                              label={label}
                              size="small"
                              onClick={() => !isDisabled && handleAccessChange(pos.id, perm)}
                              sx={{
                                height: 26, fontSize: 11, fontWeight: 600, cursor: isDisabled ? 'default' : 'pointer',
                                bgcolor: isChecked ? (perm === 'can_view' ? 'rgba(52, 211, 153, 0.15)' : perm === 'can_download' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(251, 191, 36, 0.15)') : 'rgba(255,255,255,0.04)',
                                color: isChecked ? (perm === 'can_view' ? '#34d399' : perm === 'can_download' ? '#60a5fa' : '#fbbf24') : 'rgba(255,255,255,0.25)',
                                border: `1px solid ${isChecked ? (perm === 'can_view' ? 'rgba(52, 211, 153, 0.3)' : perm === 'can_download' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(251, 191, 36, 0.3)') : 'rgba(255,255,255,0.06)'}`,
                                opacity: isDisabled ? 0.3 : 1,
                                transition: 'all 0.15s ease',
                                '&:hover': !isDisabled ? { transform: 'scale(1.05)' } : {},
                              }}
                            />
                          );
                        })}
                        <IconButton size="small" onClick={() => handleAccessChange(pos.id, 'remove')} sx={{ ml: 0.5, color: 'rgba(255,255,255,0.2)', '&:hover': { color: '#ef4444', bgcolor: 'rgba(239, 68, 68, 0.1)' } }}>
                          <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
            </>)}

            {/* Flags */}
            <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={formData.is_confidential}
                    onChange={(e) => setFormData({ ...formData, is_confidential: e.target.checked })}
                    sx={{ color: 'rgba(255,255,255,0.3)', '&.Mui-checked': { color: '#ef4444' } }}
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <LockIcon sx={{ fontSize: 14, color: formData.is_confidential ? '#ef4444' : 'rgba(255,255,255,0.3)' }} />
                    <Typography variant="caption" sx={{ color: formData.is_confidential ? '#fca5a5' : 'rgba(255,255,255,0.5)', fontWeight: 600 }}>Confidential</Typography>
                  </Box>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={formData.require_acknowledgment}
                    onChange={(e) => setFormData({ ...formData, require_acknowledgment: e.target.checked })}
                    sx={{ color: 'rgba(255,255,255,0.3)', '&.Mui-checked': { color: '#818cf8' } }}
                  />
                }
                label={
                  <Typography variant="caption" sx={{ color: formData.require_acknowledgment ? '#c7d2fe' : 'rgba(255,255,255,0.5)', fontWeight: 600 }}>Require acknowledgment</Typography>
                }
              />
            </Box>
          </Box>

          {/* Section: File Upload (Create) or Upload New Version (Edit) */}
          {(
            <Box sx={{
              p: 2.5, borderRadius: 3,
              bgcolor: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.06)',
            }}>
              <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.35)', letterSpacing: 2, fontSize: 10, fontWeight: 700, mb: 1.5, display: 'block' }}>{editingDoc ? 'Upload New Version' : 'Attachment'}</Typography>
              {uploadFile ? (
                <Box sx={{
                  py: 2, px: 2.5, borderRadius: 2.5,
                  border: '1px solid rgba(52, 211, 153, 0.3)',
                  bgcolor: 'rgba(52, 211, 153, 0.05)',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                    <UploadIcon sx={{ fontSize: 22, color: '#34d399', flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13, color: '#34d399', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {uploadFile.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(52, 211, 153, 0.7)', fontSize: 11 }}>
                        {(uploadFile.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => setUploadFile(null)}
                    sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#ef4444', bgcolor: 'rgba(239,68,68,0.1)' } }}
                  >
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  sx={{
                    py: 3, borderRadius: 2.5,
                    borderStyle: 'dashed',
                    borderColor: 'rgba(255,255,255,0.1)',
                    color: 'rgba(255,255,255,0.5)',
                    bgcolor: 'rgba(255,255,255,0.02)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.04)',
                      borderColor: 'rgba(255,255,255,0.2)',
                    }
                  }}
                >
                  <Box textAlign="center">
                    <UploadIcon sx={{ fontSize: 28, mb: 0.5, opacity: 0.7 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13 }}>
                      Choose File or Drag & Drop
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 10 }}>
                      Supported: .pdf, .doc, .docx, .xls, .xlsx, .txt, .rtf, .xml, .png, .jpg, .gif, .webp (Max 50MB)
                    </Typography>
                  </Box>
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.rtf,.xml,.png,.jpg,.jpeg,.gif,.webp"
                    onChange={(e) => handleFileSelect(e.target.files[0])}
                  />
                </Button>
              )}

              <Box display="flex" gap={2} mt={2} alignItems="center">
                <TextField
                  label="Version"
                  value={formData.version_number || '1.0'}
                  onChange={(e) => setFormData({ ...formData, version_number: e.target.value })}
                  size="small"
                  sx={{
                    width: 90,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'rgba(255,255,255,0.04)', color: 'white', borderRadius: 2,
                      '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.4)' },
                  }}
                />
                <Chip label="Draft" size="small" sx={{ bgcolor: 'rgba(100, 116, 139, 0.15)', color: '#94a3b8', border: '1px solid rgba(100, 116, 139, 0.2)', fontWeight: 600, fontSize: 11 }} />
              </Box>
            </Box>
          )}

        </DialogContent>
        <Box sx={{ px: 3, py: 2, borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'flex-end', gap: 1.5, background: 'rgba(0,0,0,0.15)' }}>
          <Button onClick={() => setOpenDialog(false)} sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 2.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}>Cancel</Button>
          <Button
            onClick={handleSaveDocument}
            variant="contained"
            disabled={saving}
            sx={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              '&:hover': { background: 'linear-gradient(135deg, #4f46e5, #7c3aed)', transform: 'translateY(-1px)' },
              boxShadow: '0 4px 16px rgba(99, 102, 241, 0.3)',
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              px: 3,
              minWidth: 160,
              transition: 'all 0.2s ease',
              '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.2)' }
            }}
          >
            {saving ? (
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 18, height: 18, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 0.8s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
                {uploadFile ? 'Uploading...' : 'Saving...'}
              </Box>
            ) : (editingDoc ? 'Save Changes' : (uploadFile ? 'Create Document' : 'Save as Draft'))}
          </Button>
        </Box>
      </Dialog>

      {/* Review Workflow Dialog */}
      <Dialog
        open={openReviewDialog}
        onClose={() => {
          setOpenReviewDialog(false);
          setSelectedDocument(null);
          setReviewAction(null);
          setReviewNotes('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {reviewAction === 'complete' ? 'Complete Review' : 'Submit for Review'}
        </DialogTitle>
        <DialogContent>
          {selectedDocument && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {selectedDocument.title}
            </Alert>
          )}
          {reviewAction === 'submit' && (
            <TextField
              margin="normal"
              fullWidth
              type="date"
              label="Review Due Date"
              InputLabelProps={{ shrink: true }}
              value={reviewDueDate}
              onChange={(e) => setReviewDueDate(e.target.value)}
            />
          )}
          <TextField
            margin="normal"
            fullWidth
            multiline
            rows={3}
            label={reviewAction === 'complete' ? 'Review Notes' : 'Instructions / Context'}
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenReviewDialog(false);
              setSelectedDocument(null);
              setReviewAction(null);
              setReviewNotes('');
            }}
          >
            Cancel
          </Button>
          {reviewAction === 'complete' ? (
            <>
              <Button
                onClick={() => handleReviewActionSubmit('reject')}
                variant="outlined"
                color="error"
              >
                Reject
              </Button>
              <Button
                onClick={() => handleReviewActionSubmit('approve')}
                variant="contained"
                color="success"
              >
                Approve Review
              </Button>
            </>
          ) : (
            <Button
              onClick={() => handleReviewActionSubmit()}
              variant="contained"
              disabled={!reviewDueDate}
            >
              Submit for Review
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Upload Version Dialog — Modern Glassmorphism */}
      <Dialog
        open={openVersionDialog}
        onClose={() => { setOpenVersionDialog(false); setUploadFile(null); setUploadSourceFile(null); setUploadChangeSummary(''); }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'rgba(15, 23, 42, 0.95)',
            backdropFilter: 'blur(40px)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 4,
            boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
          }
        }}
      >
        {/* Header */}
        <Box sx={{ px: 3, pt: 3, pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', fontSize: '1.1rem' }}>
            Upload New Version
          </Typography>
          {selectedDocument && (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', mt: 0.5, display: 'block' }}>
              {selectedDocument.title} — Current: v{selectedDocument.current_version}
            </Typography>
          )}
        </Box>

        <DialogContent sx={{ px: 3, py: 2 }}>
          {/* Version Number */}
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.35)', letterSpacing: 2, fontSize: 10, fontWeight: 700, mb: 1, display: 'block' }}>
              Version Number
            </Typography>
            <TextField
              value={uploadVersionNumber}
              onChange={(e) => setUploadVersionNumber(e.target.value)}
              size="small"
              fullWidth
              placeholder={selectedDocument ? `e.g. ${(parseFloat(selectedDocument.current_version) + 1).toFixed(1)}` : '2.0'}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255,255,255,0.04)', color: 'white', borderRadius: 2,
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                  '&.Mui-focused fieldset': { borderColor: '#6366f1' },
                },
              }}
            />
          </Box>

          {/* File Upload — Drag & Drop Style */}
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.35)', letterSpacing: 2, fontSize: 10, fontWeight: 700, mb: 1.5, display: 'block' }}>
              Attachment
            </Typography>
            {uploadFile ? (
              <Box sx={{
                py: 2, px: 2.5, borderRadius: 2.5,
                border: '1px solid rgba(52, 211, 153, 0.3)',
                bgcolor: 'rgba(52, 211, 153, 0.05)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                  <UploadIcon sx={{ fontSize: 22, color: '#34d399', flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13, color: '#34d399', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {uploadFile.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(52, 211, 153, 0.7)', fontSize: 11 }}>
                      {(uploadFile.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setUploadFile(null)}
                  sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#ef4444', bgcolor: 'rgba(239,68,68,0.1)' } }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            ) : (
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{
                  py: 3, borderRadius: 2.5,
                  borderStyle: 'dashed',
                  borderColor: 'rgba(255,255,255,0.1)',
                  color: 'rgba(255,255,255,0.5)',
                  bgcolor: 'rgba(255,255,255,0.02)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.04)',
                    borderColor: 'rgba(255,255,255,0.2)',
                  }
                }}
              >
                <Box textAlign="center">
                  <UploadIcon sx={{ fontSize: 28, mb: 0.5, opacity: 0.7 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13 }}>
                    Choose File or Drag & Drop
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 10 }}>
                    Supported: .pdf, .doc, .docx, .xls, .xlsx, .txt, .rtf, .xml, .png, .jpg, .gif, .webp (Max 50MB)
                  </Typography>
                </Box>
                <input
                  type="file"
                  hidden
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.rtf,.xml,.png,.jpg,.jpeg,.gif,.webp"
                  onChange={(e) => handleFileSelect(e.target.files[0])}
                />
              </Button>
            )}
          </Box>

          {/* Change Summary */}
          <Box>
            <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.35)', letterSpacing: 2, fontSize: 10, fontWeight: 700, mb: 1, display: 'block' }}>
              Change Summary
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={uploadChangeSummary}
              onChange={(e) => setUploadChangeSummary(e.target.value)}
              placeholder="Describe what changed in this version..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255,255,255,0.04)', color: 'white', borderRadius: 2,
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                  '&.Mui-focused fieldset': { borderColor: '#6366f1' },
                },
              }}
            />
          </Box>
        </DialogContent>

        <Box sx={{ px: 3, py: 2, borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'flex-end', gap: 1.5, background: 'rgba(0,0,0,0.15)' }}>
          <Button
            onClick={() => { setOpenVersionDialog(false); setUploadFile(null); setUploadSourceFile(null); setUploadChangeSummary(''); }}
            sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 2.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}
          >Cancel</Button>
          <Button
            onClick={handleUploadVersion}
            variant="contained"
            disabled={!uploadFile}
            sx={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              '&:hover': { background: 'linear-gradient(135deg, #4f46e5, #7c3aed)', transform: 'translateY(-1px)' },
              boxShadow: '0 4px 16px rgba(99, 102, 241, 0.3)',
              textTransform: 'none', fontWeight: 700, borderRadius: 2, px: 3, minWidth: 140,
              transition: 'all 0.2s ease',
              '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.2)' }
            }}
          >Upload Version</Button>
        </Box>
      </Dialog>

      {/* Workflow History Dialog */}
      <Dialog
        open={showWorkflowHistory && !!selectedDocument}
        onClose={() => setShowWorkflowHistory(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Workflow History: {selectedDocument?.title}</DialogTitle>
        <DialogContent dividers>
          {selectedDocument?.workflow_history?.length > 0 ? (
            <Stack spacing={2}>
              {selectedDocument.workflow_history.map((entry, index) => (
                <Box key={index} p={2} border="1px solid #eee" borderRadius={1}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Chip
                      label={entry.action.replace(/_/g, ' ').toUpperCase()}
                      size="small"
                      color={entry.action === 'approved' ? 'success' : 'default'}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(entry.acted_at)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    By: {entry.acted_by_user ? `${entry.acted_by_user.first_name} ${entry.acted_by_user.last_name}` : 'System'}
                  </Typography>
                  {entry.notes && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic', bgcolor: '#f8f9fa', p: 1, borderRadius: 1 }}>
                      "{entry.notes}"
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography align="center" color="text.secondary" py={2}>
              No workflow history available.
            </Typography>
          )}

          {selectedDocument?.versions?.length > 0 && (
            <Box mt={3}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>Version History</Typography>
              <Stack spacing={1}>
                {selectedDocument.versions.map((version) => {
                  const isCurrent = version.version_number === selectedDocument.current_version;
                  const isObsolete = version.version_number < selectedDocument.current_version;

                  return (
                    <Paper key={version.id} variant="outlined" sx={{ p: 1.5, position: 'relative', overflow: 'hidden' }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={2}>
                          <FileThumbnail
                            file={{ name: selectedDocument.title, type: selectedDocument.mime_type || 'application/pdf', id: selectedDocument.id }}
                            size={56}
                            onClick={() => handleView(selectedDocument.id, version.version_number)}
                            fetcher={() => handleThumbnailFetch(selectedDocument, version.version_number)}
                            isRestricted={selectedDocument.access_level === 'restricted'}
                          />
                          <Box display="flex" flexDirection="column" gap={0.5}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Chip
                                label={`v${version.version_number}`}
                                size="small"
                                color={isCurrent ? "primary" : "default"}
                                variant={isCurrent ? "filled" : "outlined"}
                              />
                              {isObsolete && (
                                <Typography
                                  component="span"
                                  sx={{
                                    fontWeight: 'bold',
                                    color: 'error.main',
                                    fontSize: '0.75rem',
                                    letterSpacing: '0.05em',
                                    border: '1px solid',
                                    borderColor: 'error.main',
                                    borderRadius: 1,
                                    px: 0.5
                                  }}
                                >
                                  OBSOLETE
                                </Typography>
                              )}
                              <Typography variant="body2" fontWeight="medium">
                                {version.created_by_user ? `${version.created_by_user.first_name} ${version.created_by_user.last_name}` : 'Unknown'}
                              </Typography>
                            </Box>

                            <Typography variant="caption" color="text.secondary">
                              {formatDate(version.created_at)}
                            </Typography>

                            {version.change_summary && (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                {version.change_summary}
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        <Box display="flex" flexDirection="column" gap={2} alignItems="center">
                          <Box display="flex" gap={1}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<ViewIcon />}
                              disabled={!canView || !version.file_path || isViewLoading}
                              onClick={() => handleView(selectedDocument.id, version.version_number)}
                            >
                              {isViewLoading ? 'Loading...' : 'View'}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<DownloadIcon />}
                              disabled={!canDownload || !version.file_path}
                              onClick={() => handleDownload(selectedDocument.id, version.version_number)}
                            >
                              Download
                            </Button>
                          </Box>

                          {(canView || canDownload) && (
                            <Typography variant="caption" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CheckIcon fontSize="inherit" />
                              permission to {canView && 'view'}{canView && canDownload && '/'}{canDownload && 'download'}
                            </Typography>
                          )}
                          {!canView && !canDownload && (
                            <Typography variant="caption" color="error.main">
                              No view/download permissions
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowWorkflowHistory(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <FilePreviewDialog
        open={openViewDialog}
        onClose={() => {
          setOpenViewDialog(false);
          if (viewFileData?.url) {
            window.URL.revokeObjectURL(viewFileData.url);
          }
          setViewFileData(null);
        }}
        fileData={viewFileData}
        loading={isViewLoading}
        onDownload={() => {
          if (viewFileData?.docId) {
            handleDownload(viewFileData.docId, viewFileData.versionNumber);
          }
        }}
      />

      {/* Withdraw Dialog */}
      <Dialog
        open={openWithdrawDialog}
        onClose={() => setOpenWithdrawDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Withdraw Document</DialogTitle>
        <DialogContent>
          {selectedDocument && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Are you sure you want to withdraw "{selectedDocument.title}"? This will archive the specific version and remove it from active use.
            </Alert>
          )}
          <TextField
            margin="normal"
            fullWidth
            multiline
            rows={3}
            label="Reason for Withdrawal (Optional)"
            placeholder="e.g., Replaced by newer policy, No longer applicable..."
            value={withdrawReason}
            onChange={(e) => setWithdrawReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenWithdrawDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmWithdraw} variant="contained" color="warning">
            Withdraw
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Document Dialog */}
      <Dialog
        open={shareDialog.open}
        onClose={handleShareClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, overflow: 'hidden' }
        }}
      >
        <Box sx={{ p: 3, background: 'linear-gradient(135deg, #1976d2 0%, #115293 100%)', color: 'white' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <ShieldIcon fontSize="large" sx={{ opacity: 0.9 }} />
            <Box>
              <Typography variant="h6" fontWeight="bold">Secure External Share</Typography>
            </Box>
          </Stack>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body1" paragraph>
            Generate a secure, time-limited link to share <strong>{shareDialog.docTitle}</strong> with external parties.
          </Typography>

          {!shareDialog.generatedLink ? (
            <Stack spacing={3} sx={{ mt: 2 }}>

              <Box>
                <Typography variant="subtitle2" gutterBottom display="flex" alignItems="center" gap={1}>
                  <TimerIcon fontSize="small" color="action" /> Link Expiration
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={shareDialog.days}
                    onChange={(e) => setShareDialog({ ...shareDialog, days: e.target.value })}
                    displayEmpty
                  >
                    <MenuItem value={1}>1 Day (24 Hours)</MenuItem>
                    <MenuItem value={3}>3 Days</MenuItem>
                    <MenuItem value={7}>7 Days (Standard)</MenuItem>
                    <MenuItem value={14}>14 Days</MenuItem>
                    <MenuItem value={30}>30 Days</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom display="flex" alignItems="center" gap={1}>
                  <KeyIcon fontSize="small" color="action" /> Password Protection
                </Typography>
                <TextField
                  placeholder="Optional password"
                  type={showSharePassword ? 'text' : 'password'}
                  fullWidth
                  size="small"
                  value={shareDialog.password}
                  onChange={(e) => setShareDialog({ ...shareDialog, password: e.target.value })}
                  helperText="Leave blank for no password. Recommended for Restricted/Confidential docs."
                  InputProps={{
                    startAdornment: <LockIcon color="action" fontSize="small" sx={{ mr: 1 }} />,
                    endAdornment: (
                      <IconButton
                        size="small"
                        onClick={() => setShowSharePassword(!showSharePassword)}
                        edge="end"
                      >
                        {showSharePassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    )
                  }}
                />
              </Box>
            </Stack>
          ) : (
            <Box sx={{ mt: 1 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                <strong>Link Generated Successfully!</strong>
              </Alert>

              <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Share Link (Expires in {shareDialog.days} days)
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    fullWidth
                    value={shareDialog.generatedLink}
                    InputProps={{
                      readOnly: true,
                      style: { fontSize: '0.9rem', fontFamily: 'monospace', backgroundColor: '#f8f9fa', color: '#2d3748' }
                    }}
                    size="small"
                    variant="outlined"
                  />
                  <Button
                    variant="contained"
                    onClick={() => copyToClipboard(shareDialog.generatedLink)}
                    startIcon={<CopyIcon />}
                    sx={{ minWidth: 100 }}
                  >
                    Copy
                  </Button>
                </Stack>
              </Box>

              {shareDialog.password && (
                <Alert severity="info" sx={{ mt: 2 }} icon={<KeyIcon fontSize="inherit" />}>
                  Don't forget to share the password <strong>securely</strong> via a separate channel.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={handleShareClose} size="large" color="inherit">
            {shareDialog.generatedLink ? 'Done' : 'Cancel'}
          </Button>
          {!shareDialog.generatedLink && (
            <Button
              variant="contained"
              onClick={handleCreateShareLink}
              disabled={shareDialog.loading}
              size="large"
              startIcon={shareDialog.loading ? null : <ShareIcon />}
              sx={{ px: 4 }}
            >
              {shareDialog.loading ? 'Generating...' : 'Generate Link'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Bulk Access Dialog — Modern Glassmorphism */}
      <Dialog
        open={showBulkAccessDialog}
        onClose={() => setShowBulkAccessDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#0f172a', color: 'white', borderRadius: 4,
            border: '1px solid rgba(255,255,255,0.08)',
            backgroundImage: 'none',
            overflow: 'hidden'
          }
        }}
      >
        {/* Gradient Header */}
        <Box sx={{
          p: 3, background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
          display: 'flex', alignItems: 'center', gap: 2
        }}>
          <ShieldIcon sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h6" fontWeight={800}>Bulk Access Management</Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>{selectedDocIds.size} document{selectedDocIds.size !== 1 ? 's' : ''} selected</Typography>
          </Box>
        </Box>

        <DialogContent sx={{ p: 3, maxHeight: '65vh', overflowY: 'auto' }}>
          {/* Selected Documents Table */}
          <Box sx={{ mb: 3, p: 2, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', fontSize: 11, letterSpacing: 1 }}>
              Selected Documents
            </Typography>
            <TableContainer sx={{ maxHeight: 350, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.2)' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: 'rgba(15,23,42,0.95)', color: 'rgba(255,255,255,0.5)', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Code</TableCell>
                    <TableCell sx={{ bgcolor: 'rgba(15,23,42,0.95)', color: 'rgba(255,255,255,0.5)', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Title</TableCell>
                    <TableCell sx={{ bgcolor: 'rgba(15,23,42,0.95)', color: 'rgba(255,255,255,0.5)', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Current Access</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDocuments.filter(d => selectedDocIds.has(d.id)).map(doc => {
                    const rules = doc.access_rules || [];
                    const docProcessId = doc.process_id || (doc.process && doc.process.id);
                    const processInfo = (docProcessId ? processes.find(p => p.id === docProcessId) : null) || doc.process || null;
                    return (
                    <TableRow key={doc.id}>
                      <TableCell sx={{ color: 'rgba(255,255,255,0.6)', fontFamily: 'monospace', fontSize: 12, borderBottom: '1px solid rgba(255,255,255,0.04)', verticalAlign: 'top', pt: 1.5 }}>{doc.document_code || '-'}</TableCell>
                      <TableCell sx={{ color: 'white', fontSize: 13, fontWeight: 600, borderBottom: '1px solid rgba(255,255,255,0.04)', verticalAlign: 'top', pt: 1.5 }}>{doc.title}</TableCell>
                      <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Chip
                            size="small"
                            icon={doc.access_level === 'organization_wide' ? <PublicIcon sx={{ fontSize: 14 }} /> : <LockIcon sx={{ fontSize: 14 }} />}
                            label={doc.access_level === 'organization_wide' ? 'Org-Wide' : 'Restricted'}
                            sx={{
                              height: 22, fontSize: 10, fontWeight: 700, width: 'fit-content',
                              bgcolor: doc.access_level === 'organization_wide' ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)',
                              color: doc.access_level === 'organization_wide' ? '#22c55e' : '#f59e0b',
                              '& .MuiChip-icon': { color: 'inherit' }
                            }}
                          />
                          {doc.access_level === 'restricted' && (
                            <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {processInfo && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, minWidth: 48 }}>Process:</Typography>
                                  <Chip size="small" label={processInfo.title || processInfo.name} sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: 'rgba(99,102,241,0.15)', color: '#818cf8' }} />
                                </Box>
                              )}
                              {rules.length > 0 ? rules.map((rule, ri) => (
                                <Box key={ri} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, minWidth: 48 }}>
                                    {ri === 0 ? 'Position:' : ''}
                                  </Typography>
                                  <Chip size="small" label={rule.job_position?.title || rule.title || 'Unknown'} sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: 'rgba(168,85,247,0.15)', color: '#c084fc' }} />
                                  {rule.can_view && <Chip size="small" label="View" sx={{ height: 16, fontSize: 8, fontWeight: 700, bgcolor: 'rgba(34,197,94,0.15)', color: '#22c55e' }} />}
                                  {rule.can_download && <Chip size="small" label="Download" sx={{ height: 16, fontSize: 8, fontWeight: 700, bgcolor: 'rgba(59,130,246,0.15)', color: '#3b82f6' }} />}
                                  {rule.can_edit && <Chip size="small" label="Edit" sx={{ height: 16, fontSize: 8, fontWeight: 700, bgcolor: 'rgba(245,158,11,0.15)', color: '#f59e0b' }} />}
                                </Box>
                              )) : !processInfo && (
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, fontStyle: 'italic' }}>No rules assigned</Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Access Level Toggle */}
          <Box sx={{ mb: 3, p: 2, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', fontSize: 11, letterSpacing: 1 }}>
              Access Level
            </Typography>
            <Box display="flex" gap={1.5}>
              {[
                { value: 'organization_wide', label: 'Organization-Wide', icon: <PublicIcon sx={{ fontSize: 18 }} />, desc: 'All employees can access', color: '#22c55e' },
                { value: 'restricted', label: 'Restricted', icon: <LockIcon sx={{ fontSize: 18 }} />, desc: 'Only assigned positions', color: '#f59e0b' }
              ].map(opt => (
                <Box
                  key={opt.value}
                  onClick={() => setBulkAccessSettings(prev => ({ ...prev, access_level: opt.value }))}
                  sx={{
                    flex: 1, p: 2, borderRadius: 2.5, cursor: 'pointer',
                    border: `2px solid ${bulkAccessSettings.access_level === opt.value ? opt.color : 'rgba(255,255,255,0.06)'}`,
                    bgcolor: bulkAccessSettings.access_level === opt.value ? alpha(opt.color, 0.08) : 'transparent',
                    transition: 'all 0.2s ease',
                    '&:hover': { borderColor: alpha(opt.color, 0.4) }
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Box sx={{ color: bulkAccessSettings.access_level === opt.value ? opt.color : 'rgba(255,255,255,0.4)' }}>{opt.icon}</Box>
                    <Typography variant="body2" fontWeight={700} sx={{ color: bulkAccessSettings.access_level === opt.value ? 'white' : 'rgba(255,255,255,0.5)' }}>{opt.label}</Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>{opt.desc}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Process & Job Position Selection — only when restricted */}
          {bulkAccessSettings.access_level === 'restricted' && (
            <>
              {/* Process Selection */}
              <Box sx={{ mb: 3, p: 2, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', fontSize: 11, letterSpacing: 1 }}>
                  Assign by Process
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mb: 1.5, fontSize: 11 }}>
                  All job positions within selected processes will get access
                </Typography>
                <Autocomplete
                  multiple
                  options={processes}
                  getOptionLabel={(opt) => opt.title || opt.name || ''}
                  value={processes.filter(p => bulkAccessSettings.selected_process_ids.includes(p.id))}
                  onChange={(e, newVal) => {
                    const newProcessIds = newVal.map(p => p.id);
                    // Add job positions from new processes
                    let newRules = [...bulkAccessSettings.access_rules];
                    newVal.forEach(proc => {
                      const positions = jobPositions.filter(jp => jp.process_id === proc.id);
                      positions.forEach(pos => {
                        if (!newRules.find(r => r.job_position_id === pos.id)) {
                          newRules.push({ job_position_id: pos.id, title: pos.title, process_title: proc.title || proc.name, can_view: true, can_download: true, can_edit: false });
                        }
                      });
                    });
                    // Remove positions from deselected processes
                    const removedProcessIds = bulkAccessSettings.selected_process_ids.filter(id => !newProcessIds.includes(id));
                    const removedPositionIds = jobPositions.filter(jp => removedProcessIds.includes(jp.process_id)).map(jp => jp.id);
                    newRules = newRules.filter(r => !removedPositionIds.includes(r.job_position_id));
                    setBulkAccessSettings(prev => ({ ...prev, selected_process_ids: newProcessIds, access_rules: newRules }));
                  }}
                  renderInput={(params) => <TextField {...params} placeholder="Select processes..." size="small"
                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.04)', color: 'white', borderRadius: 2, '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' } }, '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.3)' } }}
                  />}
                  renderTags={(value, getTagProps) =>
                    value.map((opt, index) => (
                      <Chip {...getTagProps({ index })} key={opt.id} label={opt.title || opt.name} size="small"
                        sx={{ bgcolor: 'rgba(99,102,241,0.2)', color: '#a5b4fc', fontWeight: 600, fontSize: 11, '& .MuiChip-deleteIcon': { color: 'rgba(165,180,252,0.5)' } }}
                      />
                    ))
                  }
                  sx={{ '& .MuiAutocomplete-popupIndicator': { color: 'rgba(255,255,255,0.3)' }, '& .MuiAutocomplete-clearIndicator': { color: 'rgba(255,255,255,0.3)' } }}
                  PaperComponent={({ children, ...props }) => <Paper {...props} sx={{ bgcolor: '#1e293b', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }}>{children}</Paper>}
                />
              </Box>

              {/* Additional Job Position */}
              <Box sx={{ mb: 3, p: 2, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', fontSize: 11, letterSpacing: 1 }}>
                  Add Individual Job Positions
                </Typography>
                <Autocomplete
                  options={jobPositions.filter(jp => !bulkAccessSettings.access_rules.some(r => r.job_position_id === jp.id))}
                  getOptionLabel={(opt) => opt.title || ''}
                  onChange={(e, newVal) => {
                    if (newVal) {
                      const proc = processes.find(p => p.id === newVal.process_id);
                      setBulkAccessSettings(prev => ({
                        ...prev,
                        access_rules: [...prev.access_rules, { job_position_id: newVal.id, title: newVal.title, process_title: proc?.title || proc?.name || '', can_view: true, can_download: true, can_edit: false }]
                      }));
                    }
                  }}
                  value={null}
                  renderInput={(params) => <TextField {...params} placeholder="Search job positions..." size="small"
                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.04)', color: 'white', borderRadius: 2, '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' } }, '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.3)' } }}
                  />}
                  sx={{ '& .MuiAutocomplete-popupIndicator': { color: 'rgba(255,255,255,0.3)' } }}
                  PaperComponent={({ children, ...props }) => <Paper {...props} sx={{ bgcolor: '#1e293b', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }}>{children}</Paper>}
                />
              </Box>

              {/* Permission Rules Table */}
              {bulkAccessSettings.access_rules.length > 0 && (
                <Box sx={{ p: 2, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', fontSize: 11, letterSpacing: 1 }}>
                    Permissions ({bulkAccessSettings.access_rules.length} position{bulkAccessSettings.access_rules.length !== 1 ? 's' : ''})
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {bulkAccessSettings.access_rules.map((rule, idx) => (
                      <Box key={rule.job_position_id} sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 2,
                        bgcolor: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.04)'
                      }}>
                        <Box flex={1} minWidth={0}>
                          <Typography variant="body2" fontWeight={700} sx={{ color: 'white', fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{rule.title}</Typography>
                          {rule.process_title && <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 10 }}>{rule.process_title}</Typography>}
                        </Box>
                        {/* Permission Chips */}
                        {[
                          { key: 'can_view', label: 'View', color: '#22c55e' },
                          { key: 'can_download', label: 'Download', color: '#3b82f6' },
                          { key: 'can_edit', label: 'Edit', color: '#f59e0b' }
                        ].map(perm => (
                          <Chip
                            key={perm.key}
                            label={perm.label}
                            size="small"
                            onClick={() => {
                              const newRules = [...bulkAccessSettings.access_rules];
                              newRules[idx] = { ...newRules[idx], [perm.key]: !newRules[idx][perm.key] };
                              setBulkAccessSettings(prev => ({ ...prev, access_rules: newRules }));
                            }}
                            sx={{
                              height: 26, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                              bgcolor: rule[perm.key] ? alpha(perm.color, 0.15) : 'rgba(255,255,255,0.04)',
                              color: rule[perm.key] ? perm.color : 'rgba(255,255,255,0.25)',
                              border: `1px solid ${rule[perm.key] ? alpha(perm.color, 0.3) : 'rgba(255,255,255,0.06)'}`,
                              transition: 'all 0.15s ease',
                              '&:hover': { bgcolor: alpha(perm.color, 0.1) }
                            }}
                          />
                        ))}
                        {/* Remove button */}
                        <IconButton size="small" onClick={() => {
                          setBulkAccessSettings(prev => ({
                            ...prev,
                            access_rules: prev.access_rules.filter((_, i) => i !== idx)
                          }));
                        }} sx={{ color: 'rgba(255,255,255,0.2)', '&:hover': { color: '#ef4444' } }}>
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <Button onClick={() => { setShowBulkAccessDialog(false); setBulkAccessSettings({ access_level: 'organization_wide', selected_process_ids: [], access_rules: [] }); }}
            sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleBulkAccessSubmit}
            disabled={selectedDocIds.size === 0 || (bulkAccessSettings.access_level === 'restricted' && bulkAccessSettings.access_rules.length === 0)}
            sx={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              fontWeight: 700, borderRadius: 2.5, px: 3,
              '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' },
              '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.2)' }
            }}
          >
            Apply to {selectedDocIds.size} Document{selectedDocIds.size !== 1 ? 's' : ''}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <DeleteIcon color="error" />
            <Typography variant="h6">Delete Document</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. All versions and related data will be permanently deleted.
          </Alert>
          <Typography>
            Are you sure you want to delete <strong>"{deleteDialog.docTitle}"</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog
        open={bulkDeleteDialog}
        onClose={() => setBulkDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#0f172a', color: 'white', borderRadius: 4,
            border: '1px solid rgba(255,255,255,0.08)', backgroundImage: 'none'
          }
        }}
      >
        <Box sx={{ p: 3, background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 50%, #b91c1c 100%)' }}>
          <Box display="flex" alignItems="center" gap={2}>
            <DeleteIcon sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h6" fontWeight={800}>Delete {selectedDocIds.size} Document{selectedDocIds.size !== 1 ? 's' : ''}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>This action cannot be undone</Typography>
            </Box>
          </Box>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          <Alert severity="error" variant="outlined" sx={{ mb: 2, borderColor: 'rgba(239,68,68,0.3)', color: '#fca5a5', '& .MuiAlert-icon': { color: '#ef4444' } }}>
            All versions, access rules, and related data for the selected documents will be permanently deleted.
          </Alert>
          <Box sx={{ maxHeight: 200, overflowY: 'auto', borderRadius: 2, bgcolor: 'rgba(0,0,0,0.2)', p: 1.5 }}>
            {filteredDocuments.filter(d => selectedDocIds.has(d.id)).map(doc => (
              <Box key={doc.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <DescriptionIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.3)' }} />
                <Typography variant="body2" sx={{ color: 'white', fontSize: 13, fontWeight: 600 }}>{doc.title}</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', ml: 'auto', fontFamily: 'monospace', fontSize: 11 }}>{doc.document_code || '-'}</Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <Button onClick={() => setBulkDeleteDialog(false)} sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleBulkDelete}
            startIcon={<DeleteIcon />}
            sx={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              fontWeight: 700, borderRadius: 2.5, px: 3,
              '&:hover': { background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' }
            }}
          >
            Delete {selectedDocIds.size} Document{selectedDocIds.size !== 1 ? 's' : ''}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick Date Edit Popover */}
      <Popover
        open={Boolean(quickDateAnchor)}
        anchorEl={quickDateAnchor}
        onClose={() => setQuickDateAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            p: 2,
            bgcolor: '#1e293b',
            border: '1px solid rgba(255,255,255,0.1)',
            boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
            borderRadius: 2
          }
        }}
      >
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography variant="subtitle2" color="white">Edit Next Update</Typography>
          <TextField
            type="date"
            size="small"
            value={quickDateValue}
            onChange={(e) => setQuickDateValue(e.target.value)}
            InputProps={{
              sx: { bgcolor: 'rgba(255,255,255,0.05)', color: 'white', borderRadius: 1 }
            }}
          />
          <Box display="flex" gap={1} justifyContent="flex-end">
            <Button size="small" onClick={() => setQuickDateAnchor(null)} sx={{ color: 'rgba(255,255,255,0.6)' }}>Cancel</Button>
            <Button size="small" variant="contained" onClick={saveQuickDate} sx={{ bgcolor: 'primary.main' }}>Save</Button>
          </Box>
        </Box>
      </Popover>

      {/* Toast Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? 6000 : 4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', fontWeight: 600, borderRadius: 2, boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Box >
  );
};

// Reusable Document Card Component
function DocumentHierarchyCard({ doc, onEdit, onUnlink, onHistory }) {

  return (
    <Paper
      onClick={() => onEdit(doc)}
      elevation={0} // Start flat for cleaner tree look
      sx={{
        py: 2,
        px: 2.5,
        bgcolor: '#fff',
        border: '1px solid #e2e8f0',
        borderLeft: `5px solid ${STATUS_INDICATORS[doc.status] || '#cbd5e1'}`,
        borderRadius: 3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        width: '100%',
        maxWidth: 280, // Match process card width
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        position: 'relative',
        '&:hover': {
          borderColor: '#94a3b8',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          '& .action-btns': { opacity: 1 }
        }
      }}
    >
      {/* Action Buttons Container */}
      <Box
        className="action-btns"
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          display: 'flex',
          gap: 0.5,
          opacity: 0, // Hidden by default
          transition: 'opacity 0.2s',
          zIndex: 10
        }}
      >
        {onHistory && (
          <Tooltip title="Workflow History">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onHistory(doc);
              }}
              sx={{
                padding: 0.5,
                bgcolor: 'rgba(255,255,255,0.9)',
                '&:hover': { bgcolor: '#f1f5f9', color: '#0f172a' }
              }}
            >
              <HistoryIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        {onUnlink && (
          <Tooltip title="Unlink Process">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onUnlink(doc);
              }}
              sx={{
                padding: 0.5,
                bgcolor: 'rgba(255,255,255,0.9)',
                '&:hover': { bgcolor: '#fee2e2', color: '#ef4444' }
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a', lineHeight: 1.3, mb: 1, pr: 4 }}>
        {doc.title}
      </Typography>
    </Paper>
  );
};

function DocumentHierarchyNode({ process, documents, onEdit, onUnlink, onHistory, departments = [] }) {
  // Group documents by department
  const docsByDept = useMemo(() => {
    return documents.reduce((acc, doc) => {
      let deptName = 'General';
      let deptId = 'general';

      if (doc.department && doc.department.name) {
        deptId = doc.department.id;
        deptName = doc.department.name;
      } else if (doc.job_position_id) {
        const foundDept = departments.find(d => d.id === doc.job_position_id);
        if (foundDept) {
          deptId = foundDept.id;
          deptName = foundDept.name;
        }
      }

      if (!acc[deptId]) {
        acc[deptId] = { name: deptName, docs: [] };
      }
      acc[deptId].docs.push(doc);
      return acc;
    }, {});
  }, [documents, departments]);

  const deptKeys = Object.keys(docsByDept);
  const hasChildren = deptKeys.length > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mx: 2, mb: 4 }}>

      {/* 1. Process Card (Root) */}
      <Paper
        elevation={3}
        sx={{
          p: 2,
          width: 280,
          border: '1px solid #e2e8f0',
          borderTop: '4px solid #4f46e5',
          borderRadius: 3,
          bgcolor: 'white',
          position: 'relative',
          zIndex: 10,
          textAlign: 'center',
          transition: 'transform 0.2s',
          '&:hover': { transform: 'translateY(-2px)' }
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1.3 }}>
          {process.title}
        </Typography>
        {documents.length > 0 && (
          <Chip
            label={`${documents.length} linked`}
            size="small"
            sx={{ mt: 1, height: 20, fontSize: '0.65rem', bgcolor: '#f1f5f9', color: '#64748b' }}
          />
        )}
      </Paper>

      {/* 2. Connection to Departments */}
      {hasChildren && (
        <>
          {/* Main Spine from Process */}
          <Box sx={{ width: 2, height: 24, bgcolor: '#cbd5e1' }} />

          {/* Departments Container (Flex Row) */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 4, position: 'relative', pt: 0 }}>

            {deptKeys.map((deptId, index) => {
              const group = docsByDept[deptId];
              const isFirst = index === 0;
              const isLast = index === deptKeys.length - 1;
              const isOnly = deptKeys.length === 1;

              return (
                <Box key={deptId} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>

                  {/* Tree Connectors */}
                  {!isOnly && (
                    <>
                      {/* Top Horizontal Bar */}
                      <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: isFirst ? '50%' : 0,
                        right: isLast ? '50%' : 0,
                        height: 2,
                        bgcolor: '#cbd5e1'
                      }} />

                      {/* Vertical Line to Node */}
                      <Box sx={{ width: 2, height: 20, bgcolor: '#cbd5e1' }} />
                    </>
                  )}
                  {isOnly && <Box sx={{ width: 2, height: 20, bgcolor: '#cbd5e1' }} />}

                  {/* Department Node */}
                  <Box sx={{
                    py: 0.75,
                    px: 2,
                    bgcolor: '#fff',
                    borderRadius: 10,
                    border: '1px solid #cbd5e1',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    zIndex: 5,
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    minWidth: 120,
                    justifyContent: 'center'
                  }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#94a3b8' }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem' }}>
                      {group.name.toUpperCase()}
                    </Typography>
                  </Box>

                  {/* Docs connection */}
                  {group.docs.length > 0 && (
                    <>
                      <Box sx={{ width: 2, bgcolor: '#cbd5e1', height: 16, mt: -2 }} />

                      <Stack spacing={2} alignItems="center">
                        {group.docs.map((doc) => (
                          <DocumentHierarchyCard
                            key={doc.id}
                            doc={doc}
                            onEdit={onEdit}
                            onUnlink={onUnlink}
                            onHistory={onHistory}
                          />
                        ))}
                      </Stack>
                    </>
                  )}
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Box >
  );
};

function DocumentHierarchyView({ processes, documents, onEdit, onUnlink, onHistory, canUpdate, departments = [] }) {
  // Helper to categorize
  const getNormalizedType = (type) => {
    if (!type) return 'Support';
    const t = type.toLowerCase();
    if (t.includes('strategic')) return 'Strategic Planning';
    if (t.includes('operational')) return 'Operational';
    if (t.includes('realization')) return 'Realization';
    if (t.includes('support')) return 'Support';
    if (t.includes('qa') || t.includes('quality') || t.includes('improvement')) return 'QA/QC and Improvement';
    return 'Support';
  };

  // Get docs for a process
  const getProcessDocs = (processId) => documents.filter(d => d.process_id === processId);

  // Filter processes to only those with linked documents
  const hasLinkedDocs = (proc) => getProcessDocs(proc.id).length > 0;

  const strategic = processes.filter(p => getNormalizedType(p.process_type) === 'Strategic Planning' && hasLinkedDocs(p));
  const operational = processes.filter(p => getNormalizedType(p.process_type) === 'Operational' && hasLinkedDocs(p));
  const realization = processes.filter(p => getNormalizedType(p.process_type) === 'Realization' && hasLinkedDocs(p));
  const qa = processes.filter(p => getNormalizedType(p.process_type) === 'QA/QC and Improvement' && hasLinkedDocs(p));
  const support = processes.filter(p => getNormalizedType(p.process_type) === 'Support' && hasLinkedDocs(p));

  // Get unassigned docs
  const unassignedDocs = documents.filter(d => !d.process_id);

  const renderSection = (title, procList, color) => {
    if (procList.length === 0) return null;

    return (
      <Box key={title} sx={{ mb: 6 }}>
        <Box display="flex" alignItems="center" gap={2} mb={3} sx={{ borderBottom: `2px solid ${alpha(color, 0.2)}`, pb: 1 }}>
          <Box sx={{ width: 6, height: 24, borderRadius: 1, bgcolor: color }} />
          <Typography variant="h6" fontWeight="bold" color="text.primary">
            {title}
          </Typography>
          <Chip
            label={procList.length}
            size="small"
            sx={{ bgcolor: alpha(color, 0.1), color: color, fontWeight: 'bold' }}
          />
        </Box>

        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 6,
          alignItems: 'flex-start'
        }}>
          {procList.map(proc => (
            <DocumentHierarchyNode
              key={proc.id}
              process={proc}
              documents={getProcessDocs(proc.id)}
              onEdit={onEdit}
              onUnlink={canUpdate ? onUnlink : null}
              onHistory={onHistory}
              departments={departments}
            />
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 4, bgcolor: '#f8fafc', borderRadius: 3, minHeight: 700 }}>
      {/* Sections Vertical Stack */}
      {renderSection('Strategic Planning', strategic, '#8b5cf6')}
      {renderSection('Operational', operational, '#0ea5e9')}
      {renderSection('Realization', realization, '#10b981')}
      {renderSection('QA / QC & Improvement', qa, '#f59e0b')}
      {renderSection('Support', support, '#64748b')}

      {unassignedDocs.length > 0 && (
        <Box sx={{ mt: 8, pt: 8, borderTop: '2px dashed #e2e8f0' }}>
          <Typography variant="h6" sx={{ mb: 4, color: '#64748b', textAlign: 'center', fontWeight: 600 }}>
            Unassigned Documents
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
            {unassignedDocs.map(doc => (
              <Box key={doc.id} sx={{ width: 250 }}>
                <DocumentHierarchyCard
                  doc={doc}
                  onEdit={onEdit}
                  onHistory={onHistory}
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

// Helper for category label used in chart
function getCategoryLabel(category) {
  const labels = {
    policy: 'Policies',
    procedure: 'Procedures',
    work_instruction: 'Work Instructions',
    record: 'Records',
    form: 'Forms',
  };
  return labels[category] || category.charAt(0).toUpperCase() + category.slice(1);
};

export default DocumentsPage;
